<template>
  <div class="rapport-page">
    <!-- En-tête -->
    <div class="rapport-header">
      <div class="rapport-header__top">
        <div>
          <h1 class="rapport-title">Rapport d'optimisation photovoltaïque</h1>
          <div class="rapport-project-name">{{ projectName }}</div>
          <div class="rapport-site" v-if="puissanceAbonnement || raccordementAffiche">Puissance souscrite {{ puissanceSouscriteAffichee }}<span v-if="raccordementAffiche"> · {{ raccordementAffiche }}</span></div>
          <div class="rapport-site rapport-type-pro" v-if="typeProAffiche">Client {{ typeProAffiche }}</div>
        </div>
        <div class="rapport-header__actions">
          <button class="btn-pdf" @click="doGeneratePdf" :disabled="isGeneratingPdf">
            <span v-if="!isGeneratingPdf">Rapport PDF complet</span>
            <span v-if="isGeneratingPdf">Génération en cours...</span>
          </button>
        </div>

      </div>
    </div>

    <!-- Données de consommation extrapolées : même avertissement qu'en Step 3 (12/10/2026) -->
    <alerte-extrapolation :stats="statsCourbe" />

    <!-- réglages qui s'écartent des valeurs par défaut du catalogue (30/10/2026) : écran seulement, pas dans le PDF -->
    <div v-if="ecartsDefauts.length" class="hypotheses-modifiees">
      <div class="hypotheses-modifiees__titre">
        {{ ecartsDefauts.length }} réglage{{ ecartsDefauts.length > 1 ? 's' : '' }} différent{{ ecartsDefauts.length > 1 ? 's' : '' }} des valeurs par défaut
      </div>
      <table>
        <thead><tr><th>Réglage</th><th>Étude</th><th>Défaut</th><th></th></tr></thead>
        <tbody>
          <tr v-for="(e, i) in ecartsDefauts" :key="i">
            <td>{{ e.libelle }}</td>
            <td class="hypotheses-modifiees__valeur">{{ e.valeur }}</td>
            <td>{{ e.defaut }}</td>
            <td><button type="button" class="hypotheses-modifiees__lien" @click="$emit('aller-a', e.etape)">Étape {{ e.etape }}</button></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- prix de revient et marges du scénario affiché (03/11/2026, réunion avec Maxime) : écran seulement, jamais dans le PDF ;
         une marge modifiée relance le calcul de toute la matrice -->
    <div v-if="sectionsCapex.length" class="marges-rapport">
      <div class="marges-rapport__titre">
        Prix de revient et marges <span class="marges-rapport__note">(scénario affiché : {{ nombreFr(puissanceKwcResult, 2) }} kWc<template v-if="batteryCapacityKwh > 0"> / {{ nombreFr(batteryCapacityKwh, 0) }} kWh</template> · écran seulement, pas dans le PDF)</span>
        <span v-if="recalculMarges" class="marges-rapport__calcul">Recalcul…</span>
      </div>
      <div class="marges-rapport__corps">
        <table class="marges-table">
          <thead>
            <tr><th>Section</th><th>Prix de revient HT</th><th>Ratio</th><th>Marge</th><th>Prix de vente HT</th></tr>
          </thead>
          <tbody>
            <tr v-for="sec in sectionsCapex" :key="sec.section" :data-section="sec.section">
              <td>{{ libelleSection(sec.section) }}</td>
              <td>{{ formatEur(sec.achat) }}</td>
              <td>{{ ratioSection(sec) }}</td>
              <td>
                <template v-if="sec.margePct != null">
                  <input class="marge-input" type="number" min="0" max="99" step="1" :value="arrondiMarge(sec.margePct)"
                    :class="{ 'marge-input--sous': sec.margeMinPct != null && sec.margePct < sec.margeMinPct - 1e-9 }"
                    :aria-label="'Marge ' + libelleSection(sec.section)" @change="changerMarge(sec.section, $event.target.value)" /> %
                  <span v-if="sec.margeMinPct != null && sec.margePct < sec.margeMinPct - 1e-9" class="marge-avertissement">min. {{ nombreFr(sec.margeMinPct, 0) }} %</span>
                </template>
                <span v-else class="marge-sans">sans marge</span>
              </td>
              <td>{{ formatEur(sec.venteHT) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr><td colspan="4">Prix de vente sans batterie</td><td>{{ formatEur(venteSansBatterie) }} <span class="marges-ratio">{{ ratioWc(venteSansBatterie) }}</span></td></tr>
            <tr v-if="venteBatterie > 0"><td colspan="4">Prix de vente de la batterie</td><td>{{ formatEur(venteBatterie) }} <span class="marges-ratio">{{ ratioKwh(venteBatterie) }}</span></td></tr>
            <tr class="marges-total"><td colspan="4">Prix de vente total HT</td><td>{{ formatEur(venteSansBatterie + venteBatterie) }} <span class="marges-ratio">{{ ratioWc(venteSansBatterie + venteBatterie) }}</span></td></tr>
          </tfoot>
        </table>
        <div class="marges-resume">
          <div class="marges-resume__titre">Matériel</div>
          <dl>
            <template v-for="(v, k) in resumeMateriel">
              <dt :key="'k' + k">{{ k }}</dt><dd :key="'v' + k">{{ v }}</dd>
            </template>
          </dl>
          <template v-if="garantiesMateriel.length">
            <div class="marges-resume__titre">Garanties</div>
            <ul><li v-for="(g, i) in garantiesMateriel" :key="i">{{ g }}</li></ul>
          </template>
        </div>
      </div>
      <p v-if="erreurMarges" class="marges-erreur">{{ erreurMarges }}</p>
    </div>

    <!-- Layout principal : sidebar gauche + corps -->
    <div class="rapport-layout">

      <!-- Barre latérale gauche (sticky) -->
      <div class="rapport-sidebar">

        <!-- Matrice de sélection manuelle -->
        <div class="sidebar-block" v-if="matrixResults && matrixResults.length">
          <div class="sidebar-block__title">Rentabilité (ans)</div>
          <div class="sidebar-block__hint">ROI (ans) selon couple PV / Stockage</div>
          <div class="matrix-axis-label matrix-axis-label--top">Stockage (kWh) →</div>
          <div class="matrix-layout">
            <div class="matrix-axis-label matrix-axis-label--left">PV (kWc) ↓</div>
            <div class="matrix-table-wrapper">
              <table class="matrix-table">
                <thead>
                  <tr>
                    <th class="matrix-corner"></th>
                    <th v-for="kwh in matrixKwhLabels" :key="'h-' + kwh" class="matrix-col-header">{{ kwh }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="kwc in matrixKwcLabels" :key="'r-' + kwc">
                    <th class="matrix-row-header" :data-kwc="kwc">{{ nombreFr(kwc, kwc % 1 ? 1 : 0) }}</th>
                    <td
                      v-for="kwh in matrixKwhLabels"
                      :key="'c-' + kwc + '-' + kwh"
                      class="matrix-cell"
                      :class="{ 'matrix-cell--selected': isSelectedCell(kwc, kwh) }"
                      @click="selectMatrixCell(kwc, kwh, $event)"
                    >
                      <span v-if="matrixCellRoi(kwc, kwh) !== null"
                        class="matrix-roi"
                        :style="{ color: matrixRoiColor(kwc, kwh) }">
                        {{ nombreFr(matrixCellRoi(kwc, kwh), 1) }}
                      </span>
                      <!-- scénario calculé mais pas rentable sur 30 ans (13/10/2026 ; 20 ans jusqu'au 27/10/2026) -->
                      <span v-else-if="celluleCalculee(kwc, kwh)" class="matrix-roi matrix-roi--non-rentable">&gt; 30</span>
                      <span v-else class="matrix-roi matrix-roi--na">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="matrix-legend-row">
            <span class="legend-item"><span class="legend-dot" style="background:#2e7d32"></span>&lt;8</span>
            <span class="legend-item"><span class="legend-dot" style="background:#66bb6a"></span>8–12</span>
            <span class="legend-item"><span class="legend-dot" style="background:#f9a825"></span>12–16</span>
            <span class="legend-item"><span class="legend-dot" style="background:#e65100"></span>16–20</span>
            <span class="legend-item"><span class="legend-dot" style="background:#b71c1c"></span>&gt;20</span>
          </div>
          <div class="matrix-hint-click">Cliquez sur une valeur pour afficher ce scénario</div>
        </div>

        <!-- Taux d'autoconsommation et d'autoproduction par scénario (TODO du 02/10/2026) -->
        <template v-if="matrixResults && matrixResults.length">
        <div class="sidebar-block taux-block" v-for="m in matricesTaux" :key="m.cle">
          <div class="sidebar-block__title">{{ m.titre }}</div>
          <div class="sidebar-block__hint">{{ m.aide }}</div>
          <div class="matrix-axis-label matrix-axis-label--top">Stockage (kWh) →</div>
          <div class="matrix-layout">
            <div class="matrix-axis-label matrix-axis-label--left">PV (kWc) ↓</div>
            <div class="matrix-table-wrapper">
              <table class="taux-table" :data-taux="m.cle">
                <thead>
                  <tr>
                    <th class="matrix-corner"></th>
                    <th v-for="kwh in matrixKwhLabels" :key="m.cle + '-h-' + kwh" class="matrix-col-header">{{ kwh }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="kwc in matrixKwcLabels" :key="m.cle + '-r-' + kwc">
                    <th class="matrix-row-header" :data-kwc="kwc">{{ nombreFr(kwc, kwc % 1 ? 1 : 0) }}</th>
                    <td
                      v-for="kwh in matrixKwhLabels"
                      :key="m.cle + '-c-' + kwc + '-' + kwh"
                      class="taux-cell"
                      :class="{ 'taux-cell--selected': isSelectedCell(kwc, kwh) }"
                      @click="selectMatrixCell(kwc, kwh, $event)"
                    >
                      <span v-if="matrixCellTaux(m.cle, kwc, kwh) !== null" class="matrix-roi"
                        :style="{ color: tauxColor(matrixCellTaux(m.cle, kwc, kwh)) }">
                        {{ nombreFr(matrixCellTaux(m.cle, kwc, kwh), 0) }}
                      </span>
                      <span v-else class="matrix-roi matrix-roi--na">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        </template>

        <!-- Avertissement : aucune référence catalogue dans la plage -->
        <div class="sidebar-block sidebar-block--warning"
             v-if="availableBatteryKwh !== null && availableBatteryKwh.length === 0">
          <div class="warning-icon">⚠</div>
          <div class="warning-text">
            Aucune capacité batterie disponible dans votre catalogue pour la plage
            [{{ batRangeMin }} – {{ batRangeMax }} kWh].
            Ajustez la plage en Step 4 pour inclure une référence catalogue.
          </div>
        </div>
      </div>

      <!-- Corps du rapport -->
      <div class="rapport-main">
        <div class="rapport-body" v-if="result">

          <!-- ═══ 1. CARACTÉRISTIQUES / INVESTISSEMENT / INDICATEURS ═══ -->
          <div class="outer-grid">
            <div class="left-col">

              <!-- Caractéristiques de l'installation -->
              <div class="info-block">
                <div class="info-block__title">Caractéristiques de l'installation</div>
                <div class="spec-cards">
                  <div class="spec-card">
                    <div class="spec-card__label">Puissance installée</div>
                    <div class="spec-card__value">{{ nombreFr(puissancePv, 2) }} kWc</div>
                  </div>
                  <div class="spec-card" v-if="avecBatterieResult">
                    <div class="spec-card__label">Capacité batterie</div>
                    <div class="spec-card__value">{{ nombreFr(batteryCapacityKwh, 1) }} kWh</div>
                    <div class="spec-card__sub">{{ cyclesBatterieAn }} cycles/an</div>
                  </div>
                  <div class="spec-card" :class="!avecBatterieResult ? 'spec-card--wide' : ''">
                    <div class="spec-card__label">Production annuelle</div>
                    <div class="spec-card__value">{{ formatKwh(productionAnnuelle) }} kWh</div>
                  </div>
                </div>
              </div>

              <!-- Investissement & Exploitation -->
              <div class="info-block info-block--fill">
                <div class="info-block__title">Investissement &amp; Exploitation {{ labelTaxe }}</div>

                <!-- CAPEX -->
                <div class="cost-section">
                  <div class="cost-section__header cost-section__header--capex">CAPEX</div>
                  <!-- chiffrage au matériel (03/11/2026) : CAPEX par section, détail de chaque section, TVA ligne par ligne -->
                  <div v-if="sectionsCapex.length" class="cost-rows cost-rows--sections">
                    <template v-for="sec in sectionsCapex">
                      <div :key="'s-' + sec.section" class="cost-row cost-row--section" :data-section="sec.section">
                        <span>{{ libelleSection(sec.section) }}</span>
                        <span>{{ formatEur(avecTva ? sec.venteTTC : sec.venteHT) }}</span>
                      </div>
                      <div v-for="(l, i) in lignesSection(sec.section)" :key="'l-' + sec.section + i" class="cost-row cost-row--detail">
                        <span>{{ l.libelle }}</span>
                        <span>{{ formatEur(montantLigne(l)) }}</span>
                      </div>
                    </template>
                    <div class="cost-row" v-if="coutRaccordementEnedisEur > 0">
                      <span>Raccordement ENEDIS</span>
                      <span>{{ formatEur(coutRaccordementEnedisEur) }}</span>
                    </div>
                    <div class="cost-row cost-row--total">
                      <span>Total</span>
                      <span>{{ formatEur(coutNetTTC) }}</span>
                    </div>
                  </div>
                  <div v-else class="cost-rows">
                    <div class="cost-row">
                      <span>Installation PV</span>
                      <span>{{ formatEur(coutInstallationPVResult) }}</span>
                    </div>
                    <!-- chiffrage au matériel (23/10/2026, provisoire) : détail de chaque poste, même TVA que l'installation -->
                    <div v-for="(l, i) in detailMateriel('PV')" :key="'mpv-' + i" class="cost-row cost-row--detail">
                      <span>{{ l.libelle }}</span>
                      <span>{{ formatEur(l.montant * facteurTvaInstallation) }}</span>
                    </div>
                    <div class="cost-row" v-if="avecBatterieResult">
                      <span>Installation Batterie</span>
                      <span>{{ formatEur(coutInstallationBatterieResult) }}</span>
                    </div>
                    <template v-if="avecBatterieResult">
                      <div v-for="(l, i) in detailMateriel('BATTERIE')" :key="'mbat-' + i" class="cost-row cost-row--detail">
                        <span>{{ l.libelle }}</span>
                        <span>{{ formatEur(l.montant * facteurTvaBatterie) }}</span>
                      </div>
                    </template>
                    <!-- ordre du 20/10/2026 : PV, batterie, câble, datalogger (pro), raccordement, travaux -->
                    <div class="cost-row cost-row--cable" v-if="cableLibelle">
                      <span>Câble onduleur ↔ TGBT ({{ cableLibelle }})</span>
                      <span>{{ coutCableResult > 0 ? formatEur(coutCableResult) : 'Non chiffré' }}</span>
                    </div>
                    <div class="cost-row" v-if="coutDataloggerResult > 0">
                      <span>Datalogger</span>
                      <span>{{ formatEur(coutDataloggerResult) }}</span>
                    </div>
                    <div class="cost-row" v-if="coutRaccordementEnedisEur > 0">
                      <span>Raccordement ENEDIS</span>
                      <span>{{ formatEur(coutRaccordementEnedisEur) }}</span>
                    </div>
                    <div class="cost-row" v-if="coutTravauxResult > 0">
                      <span>Travaux supplémentaires<template v-if="travauxLibelle"> ({{ travauxLibelle }})</template></span>
                      <span>{{ formatEur(coutTravauxResult) }}</span>
                    </div>
                    <div class="cost-row cost-row--total">
                      <span>Total</span>
                      <span>{{ formatEur(coutNetTTC) }}</span>
                    </div>
                  </div>
                </div>

                <!-- OPEX -->
                <div class="cost-section" style="margin-top:14px">
                  <div class="cost-section__header cost-section__header--opex">OPEX - AN 1</div>
                  <div class="cost-rows">
                    <div class="cost-row" v-if="avecMaintenanceResult">
                      <span>Maintenance</span>
                      <span>{{ formatEur(maintenanceAnnuelle) }}/an</span>
                    </div>
                    <div class="cost-row" v-if="assuranceAnnuelleResult > 0">
                      <span>Assurance</span>
                      <span>{{ formatEur(assuranceAnnuelleResult) }}/an</span>
                    </div>
                    <div class="cost-row cost-row--total" v-if="totalOpexAn1 > 0">
                      <span>Total OPEX</span>
                      <span>{{ formatEur(totalOpexAn1) }}/an</span>
                    </div>
                  </div>
                  <div v-if="totalOpexAn1 > 0" class="inflation-notice">
                    <span v-if="maintenanceAnnuelle > 0">Maintenance : +{{ hausseMaintenancePctResult }} % tous les {{ hausseMaintenancePeriodeResult }} ans</span><span v-if="maintenanceAnnuelle > 0 && assuranceAnnuelleResult > 0"> · </span><span v-if="assuranceAnnuelleResult > 0">Assurance : +{{ inflationAssuranceResult }} %/an</span>
                  </div>
                  <div v-else class="inflation-notice">Aucun frais d'exploitation compté.</div>
                </div>
              </div>
            </div>

            <!-- Indicateurs -->
            <div class="info-block info-block--stretch">
              <div class="info-block__title">Indicateurs</div>
              <div class="kpi-grid-2">
                <div class="kpi-2">
                  <div class="kpi-2__label">Économies An 1 {{ labelTaxe }}</div>
                  <div class="kpi-2__value kpi-2__value--green">{{ formatEur(economiesAn1) }}</div>
                  <div class="kpi-2__sub" v-if="totalOpexAn1 > 0">Dont −{{ formatEur(totalOpexAn1) }} OPEX/an</div>
                </div>
                <div class="kpi-2 kpi-2--roi">
                  <div class="kpi-2__label">Retour sur investissement</div>
                  <div class="kpi-2__value">{{ roi !== null ? nombreFr(roi, 1) + ' ans' : '&gt; 30 ans' }}</div>
                </div>
                <div class="kpi-2">
                  <div class="kpi-2__label">Gain net 30 ans</div>
                  <div class="kpi-2__value" :class="gainNet30ans >= 0 ? 'kpi-2__value--green' : 'kpi-2__value--red'">{{ formatEur(gainNet30ans) }}</div>
                </div>
                <div class="kpi-2">
                  <div class="kpi-2__label">Rendement économique</div>
                  <div class="kpi-2__value">{{ pourcentFr(rendementEconomique, 1) }}</div>
                  <div class="kpi-2__sub">Sur 30 ans</div>
                </div>
                <div class="kpi-2">
                  <div class="kpi-2__label">Coût moyen du kWh acheté {{ labelTaxe }}</div>
                  <div class="kpi-2__value">{{ nombreFr(coutMoyenKwh, 2) }} c€/kWh</div>
                  <div class="kpi-2__sub">Facture avant projet / consommation</div>
                </div>
                <div class="kpi-2">
                  <div class="kpi-2__label">Coût du kWh solaire {{ labelTaxe }}</div>
                  <div class="kpi-2__value">{{ nombreFr(coutKwhSolaire, 2) }} c€/kWh</div>
                  <div class="kpi-2__sub">Investissement / autoconso. 30 ans</div>
                </div>
                <div class="kpi-2" v-if="avecFinancementStore">
                  <div class="kpi-2__label">Coût total emprunt</div>
                  <div class="kpi-2__value kpi-2__value--red">{{ formatEur(coutTotalEmprunt) }}</div>
                  <div class="kpi-2__sub">Intérêts sur {{ (result && result.dureeEmprunt) ? result.dureeEmprunt : dureeEmprunt }} ans</div>
                </div>
                <div class="kpi-2 kpi-2--green">
                  <div class="kpi-2__label">CO₂ évité</div>
                  <div class="kpi-2__value">{{ nombreFr(co2Evite, 2) }} tCO₂/an</div>
                  <div class="kpi-2__sub">≈ {{ arbresPlantes }} arbres/an</div>
                </div>
              </div>
            </div>
          </div>

          <!-- ═══ 2. PRODUCTION & CONSOMMATION + JOURNÉE TYPE ═══ -->
          <div class="section-row" v-if="hasMonthlyData">
            <div class="rapport-section rapport-section--half">
              <div class="section-title">Production &amp; Consommation mensuelle</div>
              <wizard-chart
                type="bar"
                :chart-data="prodConsoChartData"
                :options="prodConsoOptions"
                :height="220"
              />
            </div>
            <div class="rapport-section rapport-section--half">
              <div class="section-header-row">
                <div class="section-title">Journée type <span class="section-title__note" title="Jour réel le plus proche de la journée moyenne du mois (consommation et production), et non plus le premier jour du mois">jour le plus représentatif du mois</span></div>
                <select v-model="selectedMonth" class="month-select">
                  <option v-for="(label, i) in MONTH_LABELS" :key="i" :value="i">{{ label }}</option>
                </select>
              </div>
              <!-- même présentation que le rapport PDF (page 9, 16/10/2026) : légende sans unités, stock en pointillé -->
              <div v-if="jourTypeData.length" class="jour-type-legende">
                <span v-for="l in jourTypeLegende" :key="l.texte" class="jour-type-legende__item">
                  <span :class="l.pointille ? 'jour-type-legende__trait' : 'jour-type-legende__anneau'" :style="{ borderColor: l.couleur }"></span>{{ l.texte }}
                </span>
              </div>
              <wizard-chart
                v-if="jourTypeData.length"
                type="line"
                :chart-data="jourTypeChartData"
                :options="jourTypeOptions"
                :height="220"
              />
              <div v-else class="no-data-msg">Données non disponibles</div>
            </div>
          </div>

          <!-- ═══ 3. DONUTS ANNUELS ═══ -->
          <div class="section-row">
            <div class="rapport-section rapport-section--half">
              <div class="section-title">Production annuelle</div>
              <div class="donut-subtitle">Taux d'autoconsommation : <strong>{{ pourcentFr(tauxAutoconso, 1) }}</strong></div>
              <div class="donut-row">
                <div class="donut-chart-wrap">
                  <wizard-chart
                    type="doughnut"
                    :chart-data="donutProductionData"
                    :options="donutOptions"
                    :height="180"
                  />
                </div>
                <div class="donut-legend">
                  <div class="donut-legend-item"><span class="donut-dot" style="background:#f5c518"></span>Autoconsommé direct — {{ formatKwh(autoconsoDirecKwh) }} kWh ({{ pourcentFr(pctAutoconsoDirec, 1) }})</div>
                  <div class="donut-legend-item" v-if="avecBatterieResult"><span class="donut-dot" style="background:#ff9800"></span>Via batterie — {{ formatKwh(autoconsoBatterieKwh) }} kWh ({{ pourcentFr(pctAutoconsoBat, 1) }})</div>
                  <div class="donut-legend-item"><span class="donut-dot" style="background:#4caf50"></span>{{ libelleSurplus }} — {{ formatKwh(surplusInjecte) }} kWh ({{ pourcentFr(pctSurplus, 1) }})</div>
                  <div class="donut-legend-item" v-if="avecBatterieResult && pertesBatterieKwh > 0"><span class="donut-dot" style="background:#e53935"></span>Pertes batterie — {{ formatKwh(pertesBatterieKwh) }} kWh ({{ pourcentFr(pctPertesBat, 1) }})</div>
                  <!-- chiffrage au matériel (28/10/2026) : production au-delà de la puissance maximale des onduleurs -->
                  <div class="donut-legend-note rapport-ecretage" v-if="pertesEcretageKwh >= 1"
                    title="Les panneaux produisent parfois plus que ce que les onduleurs peuvent sortir : cette énergie est perdue et n'est pas comptée dans la production.">
                    Non produit, onduleurs à leur puissance maximale : {{ formatKwh(pertesEcretageKwh) }} kWh par an
                  </div>
                </div>
              </div>
            </div>
            <div class="rapport-section rapport-section--half">
              <div class="section-title">Consommation annuelle</div>
              <div class="donut-subtitle">Taux d'autoproduction : <strong>{{ pourcentFr(tauxAutoprod, 1) }}</strong></div>
              <div class="donut-row">
                <div class="donut-chart-wrap">
                  <wizard-chart
                    type="doughnut"
                    :chart-data="donutConsoData"
                    :options="donutOptions"
                    :height="180"
                  />
                </div>
                <div class="donut-legend">
                  <div class="donut-legend-item"><span class="donut-dot" style="background:#f5c518"></span>Couverte par le solaire — {{ formatKwh(autoconsoDirecKwh) }} kWh ({{ pourcentFr(pctCouvertureSolaire, 1) }})</div>
                  <div class="donut-legend-item" v-if="avecBatterieResult"><span class="donut-dot" style="background:#ff9800"></span>Via batterie — {{ formatKwh(autoconsoBatterieKwh) }} kWh ({{ pourcentFr(pctCouvertureBat, 1) }})</div>
                  <div class="donut-legend-item"><span class="donut-dot" style="background:#1976d2"></span>Couverte par le réseau — {{ formatKwh(couvertureReseauKwh) }} kWh ({{ pourcentFr(pctCouvertureReseau, 1) }})</div>
                </div>
              </div>
              <div v-if="repartitionPeriodes" class="repartition-periodes">
                Consommation par période tarifaire :
                <span v-for="p in repartitionPeriodes" :key="p.code" class="repartition-periodes__item">
                  <b>{{ p.code }}</b> {{ formatKwh(p.kwh) }} kWh ({{ pourcentFr(p.pct, 0) }})
                </span>
              </div>
            </div>
          </div>

          <!-- ═══ 4. RÉPARTITION PRODUCTION MENSUELLE ═══ -->
          <div class="rapport-section" v-if="hasMonthlyData">
            <div class="section-title">Répartition de la production mensuelle</div>
            <wizard-chart
              type="bar"
              :chart-data="repartitionMensuelleData"
              :options="repartitionMensuelleOptions"
              :height="220"
            />
          </div>

          <!-- ═══ 5. IMPACT SUR LES FACTURES ═══ -->
          <div class="rapport-section" v-if="hasMonthlyData">
            <div class="section-title">Impact sur les factures</div>
            <div class="bilan-demande-row">
              <!-- Factures mensuelles -->
              <div class="factures-chart-card">
                <div class="subsection-title">Factures mensuelles (€) {{ labelTaxe }}</div>
                <wizard-chart
                  type="bar"
                  :chart-data="facturesMensuellesData"
                  :options="facturesOptions"
                  :height="200"
                />
              </div>
              <!-- Bilan annuel -->
              <div class="bilan-table-card">
                <div class="subsection-title">Bilan annuel estimé {{ labelTaxe }}</div>
                <table class="bilan-table">
                  <tbody>
                    <tr><td>Facture Avant</td><td class="text-right">{{ formatEur(factureAvant) }}</td></tr>
                    <tr class="tr-after"><td>Facture Après</td><td class="text-right">{{ formatEur(factureApres) }}</td></tr>
                    <tr class="tr-eco"><td>&nbsp;&nbsp;Économie de facture</td><td class="text-right text-green">+{{ formatEur(economieFacture) }}</td></tr>
                    <tr class="tr-sub" v-if="economieAutoconsoDirecEur > 0"><td>&nbsp;&nbsp;&nbsp;&nbsp;↳ Autoconso. directe</td><td class="text-right text-green">+{{ formatEur(economieAutoconsoDirecEur) }}</td></tr>
                    <tr class="tr-sub" v-if="avecBatterieResult && economieBatterieEur > 0"><td>&nbsp;&nbsp;&nbsp;&nbsp;↳ Via batterie</td><td class="text-right text-green">+{{ formatEur(economieBatterieEur) }}</td></tr>
                    <tr class="tr-eco" v-if="!zeroInjection"><td>&nbsp;&nbsp;Vente de surplus</td><td class="text-right text-green">+{{ formatEur(rachatSurplusRevenu) }}</td></tr>
                    <tr class="tr-total"><td><strong>Économie totale</strong></td><td class="text-right"><strong class="text-green">+{{ formatEur(economiesAn1) }}</strong></td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <!-- Demande réseau -->
            <div class="subsection-title" style="margin-top:20px">Demande sur le réseau (kWh)</div>
            <wizard-chart
              type="line"
              :chart-data="demandeReseauData"
              :options="demandeReseauOptions"
              :height="200"
            />
          </div>

          <!-- ═══ 6. PROJECTION FINANCIÈRE ═══ -->
          <div class="section-row">
            <div class="rapport-section rapport-section--grow">
              <div class="section-title">Projection financière sur 30 ans {{ labelTaxe }}</div>
              <wizard-chart
                type="bar"
                :chart-data="projectionChartData"
                :options="projectionOptions"
                :plugins="projectionPlugin"
                :height="240"
              />
            </div>
            <div class="rapport-section rapport-section--fixed">
              <div class="section-title">Détails des économies An 1 {{ labelTaxe }}</div>
              <div class="donut-subtitle">Total : <strong>{{ formatEur(economiesAn1) }}</strong></div>
              <wizard-chart
                type="doughnut"
                :chart-data="donutEconomiesData"
                :options="donutOptions"
                :height="180"
              />
              <div class="donut-legend donut-legend--compact">
                <div class="donut-legend-item"><span class="donut-dot" style="background:#f5c518"></span>Autoconso directe — {{ formatEur(economieAutoconsoDirecEur) }}</div>
                <div class="donut-legend-item" v-if="avecBatterieResult"><span class="donut-dot" style="background:#ff9800"></span>Via batterie — {{ formatEur(economieBatterieEur) }}</div>
                <div class="donut-legend-item" v-if="!zeroInjection"><span class="donut-dot" style="background:#4caf50"></span>Vente de surplus — {{ formatEur(rachatSurplusRevenu) }}</div>
              </div>
            </div>
          </div>

          <!-- ═══ 7. TABLEAU D'AMORTISSEMENT ═══ -->
          <div class="rapport-section">
            <div class="section-title">Tableau d'amortissement et de trésorerie {{ labelTaxe }}</div>
            <div v-for="partie in partiesAmortissement" :key="partie.titre" class="amort-partie">
            <div class="amort-partie__titre">{{ partie.titre }}</div>
            <div class="amort-table-wrapper">
              <table class="amort-table">
                <thead>
                  <tr>
                    <th>Période</th>
                    <th>Coût élec. avant</th>
                    <th>Coût élec. après</th>
                    <th>Économie / Vente</th>
                    <th v-if="avecFinancementStore">Emprunt</th>
                    <th v-if="avecMaintenanceResult">Maintenance</th>
                    <th v-if="assuranceAnnuelleResult > 0">Assurance</th>
                    <th>Résultat d'exploitation</th>
                    <th>Balance cumulée</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in partie.lignes" :key="idx"
                    :class="{ 'row-roi': row.isRoiYear, 'row-total': row.isTotal }">
                    <td><strong v-if="row.isTotal">{{ row.label }}</strong><template v-else>{{ row.label }}</template></td>
                    <td><strong v-if="row.isTotal">{{ formatEur(row.cashAvant) }}</strong><template v-else>{{ formatEur(row.cashAvant) }}</template></td>
                    <td><strong v-if="row.isTotal">{{ formatEur(row.cashApres) }}</strong><template v-else>{{ formatEur(row.cashApres) }}</template></td>
                    <td class="text-green"><strong v-if="row.isTotal">{{ row.economie > 0 ? '+' : '' }}{{ formatEur(row.economie) }}</strong><template v-else>{{ row.economie > 0 ? '+' : '' }}{{ formatEur(row.economie) }}</template></td>
                    <td v-if="avecFinancementStore"><strong v-if="row.isTotal">{{ formatEur(row.emprunt) }}</strong><template v-else>{{ formatEur(row.emprunt) }}</template></td>
                    <td v-if="avecMaintenanceResult" class="text-red"><strong v-if="row.isTotal">{{ row.maintenance > 0 ? '−' : '' }}{{ formatEur(row.maintenance) }}</strong><template v-else>{{ row.maintenance > 0 ? '−' : '' }}{{ formatEur(row.maintenance) }}</template></td>
                    <td v-if="assuranceAnnuelleResult > 0" class="text-red"><strong v-if="row.isTotal">{{ row.assurance > 0 ? '−' : '' }}{{ formatEur(row.assurance) }}</strong><template v-else>{{ row.assurance > 0 ? '−' : '' }}{{ formatEur(row.assurance) }}</template></td>
                    <td :class="row.resultat < 0 ? 'text-red' : 'text-green'"><strong>{{ formatEur(row.resultat) }}</strong></td>
                    <td :class="row.balance < 0 ? 'text-red' : 'text-green'"><strong>{{ formatEur(row.balance) }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
            </div>
          </div>

        </div>

        <!-- Pas de résultat -->
        <div v-if="!result" class="no-result">
          <p>Aucun résultat disponible. Veuillez lancer le calcul à l'étape précédente.</p>
          <button class="btn-back-calc" @click="$emit('back')">← Retour au calcul</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { raccordementParDefaut, libelleRaccordement, TVA_ELECTRICITE, TVA_TRAVAUX } from '@/store/modules/wizard.module'
import WizardChart from './WizardChart.vue'
import AlerteExtrapolation from './AlerteExtrapolation.vue'
import { messagesExtrapolation } from '@/utils/extrapolation'
import { chargerCatalogueEtude, ecartsAuxDefauts } from '@/utils/defautsEtude'
import axios from 'axios'
import { conf } from '@/config/config'
import { authenticationService } from '@/services/authentication'
import { nombreFr, pourcentFr } from '@/utils/format'
import { calculerEtude, enregistrerEtudeCalculee } from '@/services/calculEtude'

var MONTH_LABELS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre']
var MONTH_SHORT  = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc']

// Couleurs communes
var C_YELLOW  = '#f5c518'
var C_BLUE    = '#1976d2'
var C_ORANGE  = '#ff9800'
var C_GREEN   = '#4caf50'
var C_RED     = '#e53935'
var C_GREY    = '#90a4ae'
var C_NAVY    = '#1a1a2e'

function rgba(hex, a) {
  var r = parseInt(hex.slice(1,3),16)
  var g = parseInt(hex.slice(3,5),16)
  var b = parseInt(hex.slice(5,7),16)
  return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')'
}

// TRI (Taux de Rendement Interne) — taux r qui annule la VAN des flux de trésorerie :
// somme( cashFlows[t] / (1+r)^t ) = 0. Méthode standard des banquiers pour un placement
// financier (contrairement à un simple ratio gain/coût, elle tient compte de l'échelonnement
// des flux dans le temps). Résolution par dichotomie — pas de solution analytique générale.
function computeIRR(cashFlows) {
  function npv(r) {
    var total = 0
    for (var t = 0; t < cashFlows.length; t++) total += cashFlows[t] / Math.pow(1 + r, t)
    return total
  }
  var lo = -0.99, hi = 10
  var npvLo = npv(lo), npvHi = npv(hi)
  if (npvLo * npvHi > 0) return null
  for (var i = 0; i < 100; i++) {
    var mid = (lo + hi) / 2
    var npvMid = npv(mid)
    if (Math.abs(npvMid) < 1e-6) return mid
    if ((npvLo < 0) === (npvMid < 0)) { lo = mid; npvLo = npvMid } else { hi = mid }
  }
  return (lo + hi) / 2
}

var DEFAULT_SCALES = {
  xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#444', fontSize: 11 } }],
  yAxes: [{ gridLines: { color: '#f0f0f0' }, ticks: { fontColor: '#444', fontSize: 11 } }],
}

/**
 * OPEX de l'année yr (1 à 20), règle de Maxime du 08/10/2026 : la maintenance augmente de hausseMaintenancePct % tous les
 * hausseMaintenancePeriode ans (années 1 à 5 au prix de départ, 6 à 10 +2 %… avec les valeurs par défaut) ; l'assurance
 * augmente de inflationAssurance % chaque année.
 */
function evolutionOpex(maintenance, assurance, yr, p) {
  var periode = p.hausseMaintenancePeriode > 0 ? p.hausseMaintenancePeriode : 5
  return {
    maintenance: maintenance * Math.pow(1 + p.hausseMaintenancePct / 100, Math.floor((yr - 1) / periode)),
    assurance: assurance * Math.pow(1 + p.inflationAssurance / 100, yr - 1),
  }
}

// Vente du surplus : tarif d'achat quasi fixe (EDF OA), indexé à 0,4 %/an et non sur l'inflation de l'électricité (09/10/2026)
const INDEXATION_SURPLUS = 1.004
// Études sur 30 ans (27/10/2026, accord de Maxime) ; vente du surplus pendant les 20 ans du contrat EDF OA seulement,
// surplus non valorisé ensuite (WizardService.ANNEES_PROJECTION / ANNEES_VENTE_SURPLUS côté serveur)
const ANNEES_PROJECTION = 30
const ANNEES_VENTE_SURPLUS = 20

export default {
  name: 'Step6Rapport',
  components: { WizardChart, AlerteExtrapolation },
  data: function() {
    return {
      selectedMonth: 4,
      recalculMarges: false, // marge modifiée dans le rapport : calcul en cours (03/11/2026)
      erreurMarges: '',
      statsCourbe: null, // bilan de la courbe (avertissements d'extrapolation)
      MONTH_LABELS: MONTH_LABELS,
      MONTH_SHORT: MONTH_SHORT,
      isGeneratingPdf: false,
    }
  },
  created() {
    chargerCatalogueEtude()
  },
  computed: {
    // réglages différents des valeurs par défaut du catalogue (30/10/2026)
    ecartsDefauts() {
      return ecartsAuxDefauts(this.$store.state.wizard)
    },
    ...mapState('wizard', [
      'projectName', 'simulationResult', 'consumptionCurveId',
      'matrixResults', 'selectedMatrixCell', 'avecFinancement',
      'availableBatteryKwh', 'batRangeMin', 'batRangeMax',
      'materiel', 'clientLastName', 'clientFirstName', 'clientTelephone', 'clientEmail', 'clientType', 'puissanceModuleWc', 'puissanceTotaleKwc', 'raisonSociale', 'address', 'rue', 'codePostal', 'ville',
      'typeTarif', 'tarifHP', 'tarifHC',
      'tarifHPHiver', 'tarifHCHiver', 'tarifHPEte', 'tarifHCEte', 'tarifHSC', 'tarifHSCHiver', 'tarifHSCEte', 'periodesHC', 'periodesHSC', 'periodesHCEte', 'periodesHCHiver', 'periodesHSCEte', 'periodesHSCHiver',
      'tarifAbonnementAnnuel', 'puissanceAbonnement', 'raccordement',
      'apportPct', 'tauxEmprunt', 'dureeEmprunt', 'taxeelec',
      'coutRaccordementEnedis', 'orientation', 'avecMaintenance', 'zeroInjection', 'profileSource', 'profilPredifini',
      'hausseMaintenancePct', 'hausseMaintenancePeriode', 'inflationAssurance', 'degradationPanneaux', 'degradationBatterie',
    ]),

    result: function() { return this.simulationResult },
    // zéro-injection (Step 4) : le surplus n'est pas injecté mais perdu, onduleur bridé (retour du 30/09/2026)
    puissanceSouscriteAffichee: function() { return this.puissanceAbonnement ? String(this.puissanceAbonnement).replace(/\s*kVA\s*$/i, '') + ' kVA' : '—' },
    raccordementAffiche: function() { return libelleRaccordement(this.raccordement || raccordementParDefaut(this.puissanceAbonnement)) },
    libelleSurplus: function() { return this.zeroInjection ? 'Surplus non valorisé' : 'Surplus injecté' },
    avecBatterieResult: function() { return this.result && this.result.avecBatterie },
    avecFinancementStore: function() { return this.avecFinancement },
    // true = particulier (TVA de l'installation : 5,5 % jusqu'à 9 kWc, 20 % au-delà, 19/10/2026) ; false = professionnel (HT, TVA=0)
    avecTva: function() {
      if (this.result && this.result.tauxTva !== undefined && this.result.tauxTva !== null) {
        return this.result.tauxTva > 0
      }
      // Ancien résultat sans tauxTva — utiliser le type client du store comme fallback
      return this.clientType === 'Particulier'
    },
    labelTaxe: function() { return this.avecTva ? '(TTC)' : '(HT)' },

    // ── KPIs ──
    coutNetTTC:       function() { return this.result ? (this.result.coutNetTTC || 0) : 0 },
    coutInstallationHT: function() { return this.result ? (this.result.coutInstallationHT || 0) : 0 },
    economiesAn1:     function() { return this.result ? (this.result.economiesAn1 || 0) : 0 },
    puissancePv:      function() { return this.result ? (this.result.puissanceOptimaleKwc || 0) : 0 },
    productionAnnuelle: function() { return this.result ? (this.result.productionAnnuelle || 0) : 0 },
    batteryCapacityKwh: function() { return this.result ? (this.result.batteryCapacityKwh || 0) : 0 },
    cyclesBatterieAn: function() { return this.result ? (this.result.cyclesBatterieAn || 0) : 0 },
    roi: function() {
      var info = this.projectionRoiInfo
      if (info) return +(info.index + info.fraction).toFixed(2)
      return null  // ROI non atteint sur 30 ans
    },
    // carbonOffsetting est en kg de CO₂ (autoproduction kWh × 52 g/kWh ÷ 1000) ; affiché en tonnes
    co2Evite:         function() { return this.result ? (this.result.carbonOffsetting || 0) / 1000 : 0 },
    arbresPlantes:    function() { return Math.round(this.co2Evite * 1000 / 22) },

    // ── Énergie ──
    // Taux du titre = somme des lignes du détail (B29 : le taux entier du serveur différait du détail affiché)
    tauxAutoconso:        function() { return this.result ? this.pctAutoconsoDirec + this.pctAutoconsoBat : 0 },
    tauxAutoprod:         function() { return this.result ? this.pctCouvertureSolaire + this.pctCouvertureBat : 0 },
    surplusInjecte:       function() { return this.result ? (this.result.surplusInjecte || 0) : 0 },
    pertesEcretageKwh:    function() { return this.result ? (this.result.pertesEcretageKwh || 0) : 0 },
    autoconsoDirecKwh:    function() { return this.result ? (this.result.autoconsoDirecKwh || 0) : 0 },
    autoconsoBatterieKwh: function() { return this.result ? (this.result.autoconsoBatterieKwh || 0) : 0 },
    pertesBatterieKwh:    function() { return this.result ? (this.result.pertesBatterieKwh || 0) : 0 },
    couvertureReseauKwh:  function() { return this.result ? (this.result.couvertureReseauKwh || 0) : 0 },
    autoConsommationTotal: function() { return this.result ? (this.result.autoConsommation || 0) : 0 },

    // ── Maintenance (OPEX) ──
    avecMaintenanceResult: function() { return !!(this.result && this.result.avecMaintenance) },
    maintenanceAnnuelle:   function() { return this.result ? (this.result.maintenanceAnnuelle || 0) : 0 },

    // ── Financier ──
    factureAvant:             function() { return this.result ? (this.result.factureAvant || 0) : 0 },
    factureApres:             function() { return this.result ? (this.result.factureApres || 0) : 0 },
    economieFacture:          function() { return this.factureAvant - this.factureApres },
    rachatSurplusRevenu:      function() { return this.result ? (this.result.rachatSurplusRevenu || 0) : 0 },
    economieAutoconsoDirecEur: function() { return this.result ? (this.result.economieAutoconsoDirecEur || 0) : 0 },
    economieBatterieEur:      function() { return this.result ? (this.result.economieBatterieEur || 0) : 0 },
    // != null et non || : une inflation de 0 % saisie en Step 4 doit rester 0 (4 % seulement si absente)
    inflationElec:            function() { return this.result && this.result.inflationElec != null ? this.result.inflationElec : 3.5 },
    // Priorité : valeur figée dans le résultat → valeur du draft (études antérieures à
    // l'ajout du champ dans WizardOptimizeResult) → défaut Step 4.
    degradationPanneauxResult: function() {
      if (this.result && this.result.degradationPanneaux != null) return this.result.degradationPanneaux
      return this.degradationPanneaux != null ? this.degradationPanneaux : 0.4
    },
    degradationBatterieResult: function() {
      if (this.result && this.result.degradationBatterie != null) return this.result.degradationBatterie
      return this.degradationBatterie != null ? this.degradationBatterie : 2
    },
    mensualite:               function() { return this.result ? (this.result.mensualite || 0) : 0 },
    // Coût total du financement = intérêts payés = totalMensualités - montantEmprunté
    coutFinancement: function() {
      if (!this.avecFinancementStore || !this.result || !this.result.dureeEmprunt || !this.mensualite) return 0
      var montantEmprunte = this.coutNetTTC * (1 - (this.result.apportPct || 0) / 100)
      return this.mensualite * 12 * this.result.dureeEmprunt - montantEmprunte
    },
    // Coût total = matériel TTC + intérêts ; sans financement = coutNetTTC
    coutInvestissementTotal: function() {
      return this.coutNetTTC + this.coutFinancement
    },

    // ── chiffrage au matériel (03/11/2026) : CAPEX par section, résumé du matériel, garanties ──
    sectionsCapex: function() { return (this.result && this.result.sectionsCapex) || [] },
    resumeMateriel: function() { return (this.result && this.result.resumeMateriel) || {} },
    garantiesMateriel: function() { return (this.result && this.result.garantiesMateriel) || [] },
    puissanceKwcResult: function() { return this.result ? (this.result.puissanceOptimaleKwc || 0) : 0 },
    venteSansBatterie: function() {
      return this.sectionsCapex.filter(function(s) { return s.section !== 'BATTERIE' }).reduce(function(t, s) { return t + s.venteHT }, 0)
    },
    venteBatterie: function() {
      var b = this.sectionsCapex.find(function(s) { return s.section === 'BATTERIE' })
      return b ? b.venteHT : 0
    },
    // TVA de l'installation donnée par le serveur pour le scénario affiché (5,5 % jusqu'à 9 kWc, 20 % au-delà ; 20 % pour un calcul antérieur au 12/10/2026)
    facteurTvaInstallation: function() {
      if (!this.avecTva) return 1
      var taux = this.result && this.result.tauxTva ? this.result.tauxTva : 20
      return 1 + taux / 100
    },
    // ── CAPEX décomposé — même règle de TVA que coutNetTTC : le détail doit sommer au total affiché ──
    coutInstallationPVResult:       function() {
      var ht = this.result ? (this.result.coutInstallationPV || 0) : 0
      return ht * this.facteurTvaInstallation
    },
    // TVA de la batterie d'un particulier : 20 % (27/10/2026) ; résultat antérieur sans tauxTvaBatterie : celle de l'installation
    facteurTvaBatterie: function() {
      if (!this.avecTva) return 1
      var taux = this.result && this.result.tauxTvaBatterie != null ? this.result.tauxTvaBatterie : null
      return taux != null ? 1 + taux / 100 : this.facteurTvaInstallation
    },
    // professionnel chiffré au matériel : type de client (agricole ou ICT), aussi dans le PDF
    typeProAffiche: function() {
      if (this.clientType === 'Particulier' || !this.result || !this.result.detailMateriel) return ''
      return this.materiel && this.materiel.typePro === 'agri' ? 'agricole' : 'ICT (industrie, commerce, tertiaire)'
    },
    // batterie : 20 % pour un particulier (règle du 27/10/2026) ; résultat antérieur sans tauxTvaBatterie : taux de l'installation
    coutInstallationBatterieResult: function() {
      var ht = this.result ? (this.result.coutInstallationBatterie || 0) : 0
      if (!this.avecTva) return ht
      var taux = this.result && this.result.tauxTvaBatterie != null ? this.result.tauxTvaBatterie : null
      return taux != null ? ht * (1 + taux / 100) : ht * this.facteurTvaInstallation
    },
    // répartition de la consommation HP / HC (/ HSC) — lot 8 ; tarif unique : aucune
    repartitionPeriodes: function() {
      var r = this.result
      if (!r || this.typeTarif === 'unique') return null
      var hp = r.consoHpKwh || 0, hc = r.consoHcKwh || 0, hsc = r.consoHscKwh || 0
      var total = hp + hc + hsc
      if (total <= 0) return null
      var lignes = [{ code: 'HP', kwh: hp }, { code: 'HC', kwh: hc }]
      if (this.typeTarif === 'hsc' || this.typeTarif === 'hsc_saisonnier') lignes.push({ code: 'HSC', kwh: hsc })
      return lignes.map(function(l) { return { code: l.code, kwh: l.kwh, pct: l.kwh / total * 100 } })
    },
    matricesTaux: function() {
      return [
        { cle: 'autoconso', titre: 'Taux d\'autoconsommation (%)', aide: 'Part de la production consommée sur place' },
        { cle: 'autoprod', titre: 'Taux d\'autoproduction (%)', aide: 'Part de la consommation couverte par le solaire' },
      ]
    },
    // raccordement Enedis : sans TVA (13/10/2026), même montant à la saisie et dans le rapport
    coutRaccordementEnedisEur:      function() { return this.coutRaccordementEnedis || 0 },
    // datalogger : HT dans le résultat, même règle de TVA que le matériel (TODO du 02/10/2026)
    coutDataloggerResult: function() {
      var ht = this.result ? (this.result.coutDatalogger || 0) : 0
      return ht * this.facteurTvaInstallation
    },
    // travaux supplémentaires : toujours 20 % de TVA pour un particulier (règle du 20/10/2026), comme WizardService
    coutTravauxResult: function() {
      var ht = this.result ? (this.result.coutTravauxSupplementaires || 0) : 0
      return ht * (this.avecTva ? 1 + TVA_TRAVAUX : 1)
    },
    travauxLibelle: function() { return this.result ? (this.result.travauxSupplementairesLibelle || '') : '' },
    // câble onduleur ↔ TGBT (19/10/2026) : HT dans le résultat, même TVA que l'installation
    coutCableResult: function() {
      var ht = this.result ? (this.result.coutCable || 0) : 0
      return ht * this.facteurTvaInstallation
    },
    cableLibelle: function() { return this.result ? (this.result.cableLibelle || '') : '' },

    // ── OPEX ──
    assuranceAnnuelleResult: function() { return this.result ? (this.result.assuranceAnnuelle || 0) : 0 },
    // Évolution des OPEX (règle du 08/10/2026) : priorité au résultat (valeurs du calcul), puis au store, puis défaut
    hausseMaintenancePctResult: function() { return this.parametreOpex('hausseMaintenancePct', 2) },
    hausseMaintenancePeriodeResult: function() { return this.parametreOpex('hausseMaintenancePeriode', 5) },
    inflationAssuranceResult: function() { return this.parametreOpex('inflationAssurance', 1.5) },
    totalOpexAn1: function() { return this.maintenanceAnnuelle + this.assuranceAnnuelleResult },

    // ── Nouveaux KPIs ──
    gainNet30ans: function() {
      var b = this.projectionBalance
      return b.length ? b[b.length - 1] : 0
    },
    // Taux de Rendement Interne (TRI) du projet sur 30 ans — même méthode qu'un banquier
    // pour évaluer un placement financier (actualise chaque flux annuel, pas un simple ratio).
    rendementEconomique: function() {
      if (!this.coutInvestissementTotal || this.coutInvestissementTotal <= 0) return 0
      var tri = computeIRR(this.projectionCashFlows)
      return tri !== null ? tri * 100 : 0
    },
    // Coût moyen du kWh acheté aujourd'hui = facture annuelle avant projet (abonnement et taxes compris) / consommation
    // (TODO du 02/10/2026) — même chiffre que l'estimation de la Step 3 rapportée à la consommation
    coutMoyenKwh: function() {
      var conso = this.autoconsoDirecKwh + this.autoconsoBatterieKwh + this.couvertureReseauKwh
      return conso > 0 ? this.factureAvant / conso * 100 : 0
    },
    // Coût du kWh solaire (c€/kWh) = coût d'investissement (intérêts d'emprunt compris quand le financement est simulé)
    // / énergie autoconsommée sur 30 ans, année par année avec la dégradation des panneaux et de la batterie
    // (réponse de Maxime du 08/10/2026 ; même dégradation que la projection financière)
    coutKwhSolaire: function() {
      var direct = this.autoconsoDirecKwh, batterie = this.autoconsoBatterieKwh
      if (!this.coutNetTTC || direct + batterie <= 0) return 0
      var pan = 1 - this.degradationPanneauxResult / 100
      var bat = 1 - this.degradationBatterieResult / 100
      var energie = 0
      for (var k = 0; k < ANNEES_PROJECTION; k++) energie += direct * Math.pow(pan, k) + batterie * Math.pow(pan * bat, k)
      return this.coutInvestissementTotal / energie * 100
    },
    coutTotalEmprunt: function() {
      if (!this.avecFinancementStore || !this.result || !this.mensualite) return 0
      var duree = (this.result && this.result.dureeEmprunt) ? this.result.dureeEmprunt : (this.dureeEmprunt || 0)
      var montantEmprunte = this.coutNetTTC * (1 - (this.result.apportPct || 0) / 100)
      return this.mensualite * 12 * duree - montantEmprunte
    },

    // ── Données mensuelles ──
    monthlyData: function() {
      if (!this.result || !this.result.monthlySimulation) return []
      return this.result.monthlySimulation
    },
    hasMonthlyData: function() { return this.monthlyData.length === 12 },

    // ── Pourcentages production ──
    totalProdRef: function() {
      return Math.max(this.autoconsoDirecKwh + this.autoconsoBatterieKwh + this.surplusInjecte + this.pertesBatterieKwh, 1)
    },
    pctAutoconsoDirec: function() { return this.autoconsoDirecKwh / this.totalProdRef * 100 },
    pctAutoconsoBat:   function() { return this.autoconsoBatterieKwh / this.totalProdRef * 100 },
    pctSurplus:        function() { return this.surplusInjecte / this.totalProdRef * 100 },
    pctPertesBat:      function() { return this.pertesBatterieKwh / this.totalProdRef * 100 },

    // ── Pourcentages consommation ──
    totalConsoRef: function() {
      return Math.max(this.autoconsoDirecKwh + this.autoconsoBatterieKwh + this.couvertureReseauKwh, 1)
    },
    pctCouvertureSolaire: function() { return this.autoconsoDirecKwh / this.totalConsoRef * 100 },
    pctCouvertureBat:     function() { return this.autoconsoBatterieKwh / this.totalConsoRef * 100 },
    pctCouvertureReseau:  function() { return this.couvertureReseauKwh / this.totalConsoRef * 100 },

    // ── Journée type ──
    jourTypeData: function() {
      if (!this.hasMonthlyData) return []
      var m = this.monthlyData[this.selectedMonth]
      if (!m || !(m.firstDayHourlyProduction || []).length) return []
      var prod     = m.firstDayHourlyProduction
      var conso    = m.firstDayHourlyConsumption || []
      var purchase = m.firstDayHourlyPurchase || []
      var batStock = m.firstDayHourlyBatteryStock || []
      var result = []
      for (var h = 0; h < 24; h++) {
        result.push({
          prod:     prod[h] || 0,
          conso:    conso[h] || 0,
          purchase: purchase[h] || 0,
          batStock: batStock[h] || 0,
        })
      }
      return result
    },

    // ── Stacked mensuel ──
    monthlyStackedData: function() {
      var totalAC = Math.max(this.autoConsommationTotal, 1)
      var directPct  = this.autoconsoDirecKwh / totalAC
      var batPct     = this.autoconsoBatterieKwh / totalAC
      return this.monthlyData.map(function(m) {
        var ac      = m.autoConsumption || 0
        var direct  = ac * directPct
        var bat     = ac * batPct
        var surplus = Math.max(0, (m.production || 0) - ac)
        return { direct: direct, bat: bat, surplus: surplus }
      })
    },

    // ── Chart.js data objects ──

    prodConsoChartData: function() {
      return {
        labels: MONTH_SHORT,
        datasets: [
          {
            label: 'Production (kWh)',
            type: 'bar',
            data: this.monthlyData.map(function(m) { return Math.round(m.production || 0) }),
            backgroundColor: rgba(C_YELLOW, 0.8),
            borderColor: C_YELLOW,
            borderWidth: 1,
            yAxisID: 'y',
          },
          {
            label: 'Consommation (kWh)',
            type: 'line',
            data: this.monthlyData.map(function(m) { return Math.round(m.consumption || 0) }),
            borderColor: C_BLUE,
            backgroundColor: 'transparent',
            borderWidth: 2,
            pointRadius: 3,
            pointBackgroundColor: C_BLUE,
            fill: false,
            yAxisID: 'y',
          },
        ],
      }
    },
    prodConsoOptions: function() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: 'top', labels: { fontSize: 11, usePointStyle: true } },
        hover: { mode: 'index', intersect: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(item, data) {
              return data.datasets[item.datasetIndex].label + ' : ' + item.yLabel.toLocaleString('fr-FR') + ' kWh'
            },
          },
        },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#444', fontSize: 11 } }],
          // axe depuis 0 (16/10/2026 : partir de 400 kWh exagérait les écarts)
          yAxes: [{ id: 'y', gridLines: { color: '#f0f0f0' }, ticks: { fontColor: '#444', fontSize: 11, beginAtZero: true }, scaleLabel: { display: true, labelString: 'kWh', fontSize: 11, fontColor: '#444' } }],
        },
      }
    },

    jourTypeAvecStock: function() {
      return !!(this.avecBatterieResult && this.batteryCapacityKwh > 0 && this.jourTypeData.some(function(p) { return p.batStock > 0 }))
    },
    jourTypeLegende: function() {
      var l = [
        { texte: 'Production', couleur: C_YELLOW },
        { texte: 'Consommation', couleur: C_BLUE },
        { texte: 'Soutirage réseau', couleur: C_RED },
      ]
      if (this.jourTypeAvecStock) l.push({ texte: 'Stock batterie', couleur: C_ORANGE, pointille: true })
      return l
    },
    /** Puissances en kW (axe de gauche), stock de la batterie en % de sa capacité (axe de droite, 0 à 100 %) — comme le PDF. */
    jourTypeChartData: function() {
      var labels = []
      for (var h = 0; h < 24; h++) { labels.push(h + 'h') }
      var d = this.jourTypeData
      var datasets = [
        { label: 'Production', data: d.map(function(p) { return +(p.prod).toFixed(3) }), borderColor: C_YELLOW,
          backgroundColor: rgba(C_YELLOW, 0.15), borderWidth: 2, pointRadius: 0, fill: true, yAxisID: 'kw' },
        { label: 'Consommation', data: d.map(function(p) { return +(p.conso).toFixed(3) }), borderColor: C_BLUE,
          backgroundColor: 'transparent', borderWidth: 2, pointRadius: 0, fill: false, yAxisID: 'kw' },
        { label: 'Soutirage réseau', data: d.map(function(p) { return +(p.purchase).toFixed(3) }), borderColor: C_RED,
          backgroundColor: rgba(C_RED, 0.08), borderWidth: 2, pointRadius: 0, fill: true, yAxisID: 'kw' },
      ]
      if (this.jourTypeAvecStock) {
        var capacite = this.batteryCapacityKwh
        datasets.push({ label: 'Stock batterie', data: d.map(function(p) { return +Math.min(100, p.batStock / capacite * 100).toFixed(1) }),
          borderColor: C_ORANGE, backgroundColor: 'transparent', borderWidth: 2, borderDash: [6, 4], pointRadius: 0, fill: false, yAxisID: 'pct' })
      }
      return { labels: labels, datasets: datasets }
    },
    jourTypeOptions: function() {
      var fr = function(v, n) { return nombreFr(v, n) }
      var axes = [{ id: 'kw', position: 'left', gridLines: { color: '#f0f0f0' },
        ticks: { fontColor: '#444', fontSize: 11, beginAtZero: true, maxTicksLimit: 6, callback: function(v) { return fr(v, v % 1 ? 1 : 0) + ' kW' } } }]
      if (this.jourTypeAvecStock) {
        axes.push({ id: 'pct', position: 'right', gridLines: { display: false },
          ticks: { fontColor: C_ORANGE, fontSize: 11, min: 0, max: 100, stepSize: 25, callback: function(v) { return v + ' %' } } })
      }
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        hover: { mode: 'index', intersect: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(item, data) {
              var ds = data.datasets[item.datasetIndex]
              return ds.label + ' : ' + fr(parseFloat(item.yLabel), 1) + (ds.yAxisID === 'pct' ? ' %' : ' kW')
            },
          },
        },
        scales: { xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#444', fontSize: 11, maxRotation: 0, autoSkip: true, maxTicksLimit: 9 } }], yAxes: axes },
      }
    },

    donutProductionData: function() {
      var labels = ['Autoconsommé direct', 'Via batterie', this.libelleSurplus, 'Pertes batterie']
      var data   = [this.autoconsoDirecKwh, this.autoconsoBatterieKwh, this.surplusInjecte, this.pertesBatterieKwh]
      var colors = [C_YELLOW, C_ORANGE, C_GREEN, C_RED]
      if (!this.avecBatterieResult) {
        labels = ['Autoconsommé', this.libelleSurplus]
        data   = [this.autoconsoDirecKwh, this.surplusInjecte]
        colors = [C_YELLOW, C_GREEN]
      }
      return {
        labels: labels,
        datasets: [{ data: data.map(Math.round), backgroundColor: colors, borderWidth: 2, borderColor: '#fff' }],
      }
    },
    donutConsoData: function() {
      var labels = ['Couverte par le solaire', 'Via batterie', 'Couverte par le réseau']
      var data   = [this.autoconsoDirecKwh, this.autoconsoBatterieKwh, this.couvertureReseauKwh]
      var colors = [C_YELLOW, C_ORANGE, C_BLUE]
      if (!this.avecBatterieResult) {
        labels = ['Couverte par le solaire', 'Couverte par le réseau']
        data   = [this.autoconsoDirecKwh, this.couvertureReseauKwh]
        colors = [C_YELLOW, C_BLUE]
      }
      return {
        labels: labels,
        datasets: [{ data: data.map(Math.round), backgroundColor: colors, borderWidth: 2, borderColor: '#fff' }],
      }
    },
    donutEconomiesData: function() {
      var labels = ['Autoconso directe', 'Via batterie', 'Vente de surplus']
      var data   = [this.economieAutoconsoDirecEur, this.economieBatterieEur, this.rachatSurplusRevenu]
      var colors = [C_YELLOW, C_ORANGE, C_GREEN]
      if (!this.avecBatterieResult) {
        labels = ['Autoconso directe', 'Vente de surplus']
        data   = [this.economieAutoconsoDirecEur, this.rachatSurplusRevenu]
        colors = [C_YELLOW, C_GREEN]
      }
      return {
        labels: labels,
        datasets: [{ data: data.map(function(v) { return Math.round(v * 100) / 100 }), backgroundColor: colors, borderWidth: 2, borderColor: '#fff' }],
      }
    },
    donutOptions: function() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 65,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: function(item, data) {
              var label = data.labels[item.index] || ''
              var value = data.datasets[0].data[item.index]
              return ' ' + label + ' : ' + value.toLocaleString('fr-FR')
            },
          },
        },
      }
    },

    repartitionMensuelleData: function() {
      var stacked = this.monthlyStackedData
      var datasets = [
        {
          label: 'Autoconso. directe (kWh)',
          data: stacked.map(function(m) { return Math.round(m.direct) }),
          backgroundColor: rgba(C_YELLOW, 0.85),
          borderColor: C_YELLOW, borderWidth: 1,
        },
      ]
      if (this.avecBatterieResult) {
        datasets.push({
          label: 'Via batterie (kWh)',
          data: stacked.map(function(m) { return Math.round(m.bat) }),
          backgroundColor: rgba(C_ORANGE, 0.85),
          borderColor: C_ORANGE, borderWidth: 1,
        })
      }
      datasets.push({
        label: this.libelleSurplus + ' (kWh)',
        data: stacked.map(function(m) { return Math.round(m.surplus) }),
        backgroundColor: rgba(C_GREEN, 0.75),
        borderColor: C_GREEN, borderWidth: 1,
      })
      return { labels: MONTH_SHORT, datasets: datasets }
    },
    repartitionMensuelleOptions: function() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: 'top', labels: { fontSize: 11, usePointStyle: true } },
        hover: { mode: 'index', intersect: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(item, data) {
              return data.datasets[item.datasetIndex].label + ' : ' + item.yLabel.toLocaleString('fr-FR') + ' kWh'
            },
          },
        },
        scales: {
          xAxes: [{ stacked: true, gridLines: { display: false }, ticks: { fontColor: '#444', fontSize: 11 } }],
          yAxes: [{ stacked: true, gridLines: { color: '#f0f0f0' }, ticks: { fontColor: '#444', fontSize: 11 }, scaleLabel: { display: true, labelString: 'kWh', fontSize: 11, fontColor: '#444' } }],
        },
      }
    },

    facturesMensuellesData: function() {
      return {
        labels: MONTH_SHORT,
        datasets: [
          {
            label: 'Facture Avant (€)',
            data: this.monthlyData.map(function(m) { return Math.round((m.currentBilling || 0) * 100) / 100 }),
            backgroundColor: rgba(C_GREY, 0.7),
            borderColor: C_GREY, borderWidth: 1,
          },
          {
            label: 'Facture Après (€)',
            data: this.monthlyData.map(function(m) { return Math.round((m.newBilling || 0) * 100) / 100 }),
            backgroundColor: rgba(C_YELLOW, 0.85),
            borderColor: C_YELLOW, borderWidth: 1,
          },
        ],
      }
    },
    facturesOptions: function() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: 'top', labels: { fontSize: 11, usePointStyle: true } },
        hover: { mode: 'index', intersect: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(item, data) {
              return data.datasets[item.datasetIndex].label + ' : ' + item.yLabel.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
            },
          },
        },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#444', fontSize: 11 } }],
          yAxes: [{ gridLines: { color: '#f0f0f0' }, ticks: { fontColor: '#444', fontSize: 11 }, scaleLabel: { display: true, labelString: '€', fontSize: 11, fontColor: '#444' } }],
        },
      }
    },

    demandeReseauData: function() {
      return {
        labels: MONTH_SHORT,
        datasets: [
          {
            label: 'Soutirage Avant (kWh)',
            data: this.monthlyData.map(function(m) { return Math.round(m.consumption || 0) }),
            borderColor: C_GREY,
            backgroundColor: rgba(C_GREY, 0.15),
            borderWidth: 2,
            pointRadius: 3,
            fill: true,
          },
          {
            label: 'Soutirage Après (kWh)',
            data: this.monthlyData.map(function(m) { return Math.round(m.purchase || 0) }),
            borderColor: C_YELLOW,
            backgroundColor: rgba(C_YELLOW, 0.2),
            borderWidth: 2,
            pointRadius: 3,
            fill: true,
          },
        ],
      }
    },
    demandeReseauOptions: function() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: 'top', labels: { fontSize: 11, usePointStyle: true } },
        hover: { mode: 'index', intersect: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(item, data) {
              return data.datasets[item.datasetIndex].label + ' : ' + item.yLabel.toLocaleString('fr-FR') + ' kWh'
            },
          },
        },
        scales: DEFAULT_SCALES,
      }
    },

    // Flux de trésorerie du projet — index 0 = mise de fonds initiale (négative),
    // index 1..30 = flux net annuel (économies − OPEX) après inflation/dégradation.
    // Source unique pour projectionBalance (cumul) et rendementEconomique (TRI).
    projectionCashFlows: function() {
      var inflationEco  = 1 + this.inflationElec / 100
      var panFactor = 1 - this.degradationPanneauxResult / 100
      var batFactor = 1 - this.degradationBatterieResult / 100
      var ecoDirec = this.economieAutoconsoDirecEur
      var ecoSurp  = this.rachatSurplusRevenu
      var ecoBat   = this.economieBatterieEur
      var flows = [-this.coutInvestissementTotal]
      for (var yr = 1; yr <= ANNEES_PROJECTION; yr++) {
        var o = this.opexAnnee(yr)
        flows.push(ecoDirec + (yr <= ANNEES_VENTE_SURPLUS ? ecoSurp : 0) + ecoBat - o.maintenance - o.assurance)
        ecoDirec = ecoDirec * inflationEco * panFactor
        ecoSurp  = ecoSurp  * INDEXATION_SURPLUS * panFactor
        ecoBat   = ecoBat   * inflationEco * panFactor * batFactor
      }
      return flows
    },
    projectionBalance: function() {
      // Balance = cumul(économies - OPEX) vs coût total (matériel + intérêts).
      // L'emprunt est intégré dans coutInvestissementTotal, pas déduit annuellement ici.
      var flows = this.projectionCashFlows
      var cumul = flows[0]
      var arr = []
      for (var yr = 1; yr < flows.length; yr++) {
        cumul += flows[yr]
        arr.push(Math.round(cumul))
      }
      return arr
    },
    projectionRoiInfo: function() {
      var balance = this.projectionBalance
      var startVal = -this.coutInvestissementTotal
      for (var i = 0; i < balance.length; i++) {
        if (balance[i] >= 0) {
          var prev = i === 0 ? startVal : balance[i - 1]
          var curr = balance[i]
          var fraction = Math.abs(prev) / (Math.abs(prev) + Math.abs(curr))
          return { index: i, fraction: fraction }
        }
      }
      return null
    },
    projectionPlugin: function() {
      var info = this.projectionRoiInfo
      if (!info) return []
      var roiLabel = this.roi !== null ? 'Rentabilité : ' + nombreFr(this.roi, 1) + ' ans' : 'Rentabilité : > 30 ans'
      return [{
        afterDraw: function(chart) {
          var ctx = chart.ctx
          var meta = chart.getDatasetMeta(0)
          if (!meta || !meta.data || !meta.data.length) return

          // Position x interpolée entre le bar précédent et le bar courant
          var idx = info.index
          var fraction = info.fraction
          var currBar = meta.data[idx]
          var prevBar = idx > 0 ? meta.data[idx - 1] : null
          var xCurr = currBar._model.x
          var xPrev = prevBar ? prevBar._model.x : xCurr - currBar._model.width
          var x = xPrev + (xCurr - xPrev) * fraction

          var chartArea = chart.chartArea
          ctx.save()
          ctx.beginPath()
          ctx.setLineDash([6, 4])
          ctx.moveTo(x, chartArea.top)
          ctx.lineTo(x, chartArea.bottom)
          ctx.strokeStyle = '#e53935'
          ctx.lineWidth = 2
          ctx.stroke()

          // Étiquette ROI
          ctx.setLineDash([])
          ctx.font = 'bold 11px Raleway, sans-serif'
          ctx.fillStyle = '#e53935'
          ctx.textAlign = x > chart.width - 120 ? 'right' : 'left'
          var labelX = x > chart.width - 120 ? x - 6 : x + 6
          ctx.fillText(roiLabel, labelX, chartArea.top + 14)
          ctx.restore()
        },
      }]
    },
    projectionChartData: function() {
      var labels = []
      for (var yr = 1; yr <= ANNEES_PROJECTION; yr++) { labels.push('An ' + yr) }
      var balance = this.projectionBalance
      return {
        labels: labels,
        datasets: [{
          label: 'Bénéfice net cumulé (€)',
          data: balance,
          backgroundColor: balance.map(function(v) { return v >= 0 ? rgba(C_GREEN, 0.75) : rgba(C_RED, 0.65) }),
          borderColor: balance.map(function(v) { return v >= 0 ? C_GREEN : C_RED }),
          borderWidth: 1,
        }],
      }
    },
    projectionOptions: function() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: function(item) {
              return 'Bénéfice cumulé : ' + Math.round(item.yLabel).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
            },
          },
        },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#444', fontSize: 10 } }],
          yAxes: [{
            gridLines: { color: '#f0f0f0', zeroLineColor: '#999', zeroLineWidth: 1.5 },
            ticks: { fontColor: '#444', fontSize: 11 },
            scaleLabel: { display: true, labelString: '€', fontSize: 11, fontColor: '#444' },
          }],
        },
      }
    },

    // ── Tableau amortissement ──
    tableauAmortissement: function() {
      var rows = []
      var totalCost = this.coutInvestissementTotal
      var inflationEco  = 1 + this.inflationElec / 100
      var panFactor = 1 - this.degradationPanneauxResult / 100
      var batFactor = 1 - this.degradationBatterieResult / 100
      var ecoDirec = this.economieAutoconsoDirecEur
      var ecoSurp  = this.rachatSurplusRevenu
      var ecoBat   = this.economieBatterieEur
      var balance = -totalCost
      var empruntAnnuel = this.avecFinancementStore ? this.mensualite * 12 : 0
      var dureeEmprunt = (this.result && this.result.dureeEmprunt) ? this.result.dureeEmprunt : 0

      var totCashAvant = 0, totCashApres = 0, totEco = 0, totEmprunt = 0, totMaint = 0, totAssurance = 0, totResultat = 0

      for (var yr = 1; yr <= ANNEES_PROJECTION; yr++) {
        var o = this.opexAnnee(yr)
        var maintenance = o.maintenance
        var assurance = o.assurance
        var eco = ecoDirec + (yr <= ANNEES_VENTE_SURPLUS ? ecoSurp : 0) + ecoBat
        var annuite = (empruntAnnuel > 0 && yr <= dureeEmprunt) ? empruntAnnuel : 0
        var resultat = eco - annuite - maintenance - assurance
        balance = balance + eco - maintenance - assurance
        var cashAvant = this.factureAvant * Math.pow(inflationEco, yr - 1)
        var cashApres = this.factureApres * Math.pow(inflationEco, yr - 1)
        rows.push({
          label: 'Année ' + yr,
          cashAvant: cashAvant, cashApres: cashApres,
          economie: eco, emprunt: annuite,
          maintenance: maintenance, assurance: assurance,
          resultat: resultat, balance: balance,
          isRoiYear: this.roi !== null && yr === Math.ceil(this.roi),
        })
        totCashAvant  += cashAvant
        totCashApres  += cashApres
        totEco        += eco
        totEmprunt    += annuite
        totMaint      += maintenance
        totAssurance  += assurance
        totResultat   += resultat
        ecoDirec    = ecoDirec    * inflationEco * panFactor
        ecoSurp     = ecoSurp     * INDEXATION_SURPLUS * panFactor
        ecoBat      = ecoBat      * inflationEco * panFactor * batFactor
      }
      // Ligne Total en tête : sommes sur 30 ans ; balance = gain net final (dernière valeur cumulée)
      rows.unshift({
        label: 'Total 30 ans', cashAvant: totCashAvant, cashApres: totCashApres,
        economie: totEco, emprunt: totEmprunt, maintenance: totMaint, assurance: totAssurance,
        resultat: totResultat, balance: balance, isRoiYear: false, isTotal: true,
      })
      return rows
    },
    // deux tableaux (27/10/2026) : total et années 1 à 20, puis années 21 à 30
    partiesAmortissement: function() {
      var rows = this.tableauAmortissement
      return [
        { titre: 'Années 1 à ' + ANNEES_VENTE_SURPLUS, lignes: rows.slice(0, ANNEES_VENTE_SURPLUS + 1) },
        { titre: 'Années ' + (ANNEES_VENTE_SURPLUS + 1) + ' à ' + ANNEES_PROJECTION, lignes: rows.slice(ANNEES_VENTE_SURPLUS + 1) },
      ]
    },

    // ── Matrice ──
    matrixKwcLabels: function() {
      if (!this.matrixResults) return []
      var vals = []
      this.matrixResults.forEach(function(c) { if (vals.indexOf(c.kwc) === -1) vals.push(c.kwc) })
      return vals.sort(function(a,b) { return a-b })
    },
    matrixKwhLabels: function() {
      if (!this.matrixResults) return []
      var vals = []
      this.matrixResults.forEach(function(c) { if (vals.indexOf(c.kwh) === -1) vals.push(c.kwh) })
      return vals.sort(function(a,b) { return a-b })
    },
  },
  methods: {
    // avertissements d'extrapolation de la courbe de consommation (bilan relu sur le serveur, comme en Step 3)
    chargerStatsCourbe: function() {
      if (!this.consumptionCurveId) return
      var self = this
      axios.post(conf.API_URL + '/wizard/curve/' + this.consumptionCurveId + '/stats', {},
        { headers: { Authorization: 'Bearer ' + authenticationService.status.jwtToken } })
        .then(function(r) { if (r && r.data && r.data.data) self.statsCourbe = r.data.data })
        .catch(function() { /* pas d'avertissement si le bilan est indisponible */ })
    },
    parametreOpex: function(cle, defaut) {
      if (this.result && this.result[cle] != null) return this.result[cle]
      return this[cle] != null ? this[cle] : defaut
    },
    /** Maintenance et assurance de l'année yr (1 à 30) de l'étude affichée. */
    opexAnnee: function(yr) {
      return evolutionOpex(this.maintenanceAnnuelle, this.assuranceAnnuelleResult, yr, {
        hausseMaintenancePct: this.hausseMaintenancePctResult,
        hausseMaintenancePeriode: this.hausseMaintenancePeriodeResult,
        inflationAssurance: this.inflationAssuranceResult,
      })
    },
    ...mapMutations('wizard', ['SET_MATRIX_CELL']),

    doGeneratePdf: function() {
      if (!this.result) {
        this.$toasted.error('Aucun résultat de calcul disponible.')
        return
      }
      var self = this
      self.isGeneratingPdf = true
      var clientName = [this.clientFirstName, this.clientLastName].filter(function(s) { return s && s.trim() }).join(' ') || (this.projectName || 'Client')
      var commercial = [authenticationService.getFirstname(), authenticationService.getLastname()].filter(function(s) { return s && s.trim() }).join(' ') || authenticationService.getLogin() || ''
      var tva = this.avecTva ? 1 + TVA_ELECTRICITE : 1
      var agenceCommercial = authenticationService.getAgence() || ''
      var telephoneCommercial = authenticationService.getTelephone() || ''
      var now = new Date()
      var dateEtude = now.getDate().toString().padStart(2,'0') + '/' + (now.getMonth()+1).toString().padStart(2,'0') + '/' + now.getFullYear()
      var hasBat = !!(this.avecBatterieResult && this.batteryCapacityKwh > 0)
      var d = {
        projectName: this.projectName || '',
        clientName: clientName,
        raisonSociale: this.clientType === 'Particulier' ? 'Particulier' : (this.raisonSociale || clientName),
        clientAddress: this.rue || this.address || '',
        cpVille: (this.codePostal && this.ville) ? (this.codePostal + ' ' + this.ville) : (this.ville || this.codePostal || ''),
        commercial: commercial,
        agence: agenceCommercial,
        telephone: telephoneCommercial,
        clientTelephone: this.clientTelephone || '',
        puissanceModuleWc: this.puissanceModuleWc || 0,
        puissanceMaxKwc: this.puissanceTotaleKwc || 0,
        clientEmail: this.clientEmail || '',
        commercialEmail: authenticationService.getLogin() || '',
        dateEtude: dateEtude,
        consoAnnuelle: this.autoconsoDirecKwh + this.autoconsoBatterieKwh + this.couvertureReseauKwh,
        puissanceAbonnement: this.puissanceAbonnement || '',
        raccordement: this.raccordementAffiche,
        factureAvant: this.factureAvant,
        factureApres: this.factureApres,
        typeTarif: this.typeTarif || 'hchp',
        zeroInjection: !!this.zeroInjection,
        // prix saisis HT en Step 3 : présentés TTC au particulier (règle du 12/10/2026)
        tarifHP: (this.tarifHP || 0) * tva,
        tarifHC: (this.tarifHC || 0) * tva,
        tarifHPHiver: (this.tarifHPHiver || 0) * tva,
        tarifHCHiver: (this.tarifHCHiver || 0) * tva,
        tarifHPEte: (this.tarifHPEte || 0) * tva,
        tarifHCEte: (this.tarifHCEte || 0) * tva,
        tarifHSC: (this.tarifHSC || 0) * tva,
        periodesHC: this.periodesHC || [],
        periodesHSC: this.periodesHSC || [],
        periodesHCEte: this.periodesHCEte || [],
        periodesHCHiver: this.periodesHCHiver || [],
        periodesHSCEte: this.periodesHSCEte || [],
        periodesHSCHiver: this.periodesHSCHiver || [],
        tarifHSCHiver: (this.tarifHSCHiver || 0) * tva,
        tarifHSCEte: (this.tarifHSCEte || 0) * tva,
        consoHpKwh: this.result ? (this.result.consoHpKwh || 0) : 0,
        consoHcKwh: this.result ? (this.result.consoHcKwh || 0) : 0,
        consoHscKwh: this.result ? (this.result.consoHscKwh || 0) : 0,
        tarifAbonnement: (this.tarifAbonnementAnnuel || 0) * tva,
        taxeelec: (this.taxeelec || 0) * tva,
        hasTarifHC: this.typeTarif !== 'unique',
        puissanceKwc: this.puissancePv,
        batteryKwh: this.batteryCapacityKwh,
        cyclesBatterieAn: this.cyclesBatterieAn,
        productionAnnuelle: this.productionAnnuelle,
        orientation: this.orientation || '',
        hasBattery: hasBat,
        tauxAutoconso: this.tauxAutoconso,
        tauxAutoprod: this.tauxAutoprod,
        autoconsoDir: this.autoconsoDirecKwh,
        autoconsoBat: this.autoconsoBatterieKwh,
        pertesBat: this.pertesBatterieKwh,
        surplus: this.surplusInjecte,
        reseau: this.couvertureReseauKwh,
        coutNetTTC: this.coutNetTTC,
        coutInstallationPV: this.coutInstallationPVResult,
        detailMateriel: this.detailMaterielPdf(),
        sectionsCapex: this.sectionsCapex.map(function(sec) { return { section: sec.section, libelle: self.libelleSection(sec.section), montant: self.avecTva ? sec.venteTTC : sec.venteHT } }),
        resumeMateriel: this.resumeMateriel,
        garanties: this.garantiesMateriel,
        typePro: this.typeProAffiche ? this.typeProAffiche.charAt(0).toUpperCase() + this.typeProAffiche.slice(1) : '',
        coutInstallationBatterie: this.avecBatterieResult ? this.coutInstallationBatterieResult : 0,
        coutDatalogger: this.coutDataloggerResult,
        coutTravaux: this.coutTravauxResult,
        travauxLibelle: this.travauxLibelle,
        coutCable: this.coutCableResult,
        cableLibelle: this.cableLibelle,
        coutKwhSolaire: this.coutKwhSolaire,
        maintenanceAnnuelle: this.maintenanceAnnuelle,
        avecMaintenance: !!(this.avecMaintenanceResult),
        coutRaccordement: this.coutRaccordementEnedisEur,
        coutInvestissementTotal: this.coutInvestissementTotal,
        coutEmprunt: this.coutTotalEmprunt,
        avecFinancement: !!(this.avecFinancementStore),
        apportPct: this.apportPct || 0,
        mensualite: this.mensualite,
        tauxEmprunt: this.tauxEmprunt || 0,
        dureeEmprunt: (this.result && this.result.dureeEmprunt) ? this.result.dureeEmprunt : (this.dureeEmprunt || 0),
        economiesAn1: this.economiesAn1,
        rachatSurplus: this.rachatSurplusRevenu,
        ecoAutoconsoDir: this.economieAutoconsoDirecEur,
        ecoBat: this.economieBatterieEur,
        projectionBalance: this.projectionBalance,
        journeesTypes: (this.result && this.result.journeesTypes) || [],
        inflationElec: this.inflationElec,
        avecAssurance: this.assuranceAnnuelleResult > 0,
        assuranceAnnuelle: this.assuranceAnnuelleResult,
        amortRows: this.tableauAmortissement,
        monthlyData: this.monthlyData,
        taxeMention: this.avecTva ? '(TTC)' : '(HT)',
        avertissementsExtrapolation: messagesExtrapolation(this.statsCourbe),
        profileSource: this.profileSource || '',
        profilPredifini: this.profilPredifini || '',
      }
      import('@/services/pdfExportService').then(function(mod) {
        return mod.generatePdf(d)
      }).then(function() {
        self.isGeneratingPdf = false
      }).catch(function(err) {
        // plus de rapport de secours (19/10/2026) : message clair, l'utilisateur réessaie
        console.error('Erreur génération PDF', err)
        self.$toasted.error('Le rapport PDF n\'a pas pu être produit (service d\'impression indisponible). Réessayez dans une minute ; si le problème continue, prévenez l\'administrateur.', { duration: 10000 })
        self.isGeneratingPdf = false
      })
    },

    nombreFr: nombreFr,
    pourcentFr: pourcentFr,
    /** Lignes du chiffrage au matériel pour le PDF, TVA comprise pour un particulier (comme l'écran). */
    detailMaterielPdf: function() {
      var self = this
      // 03/11/2026 : par section (prix de vente, TVA de chaque ligne), sans prix de revient ni marge
      if (this.sectionsCapex.length) {
        var out = []
        this.sectionsCapex.forEach(function(sec) {
          out.push({ poste: sec.section, libelle: self.libelleSection(sec.section), montant: self.avecTva ? sec.venteTTC : sec.venteHT, section: true })
          self.lignesSection(sec.section).forEach(function(l) { out.push({ poste: sec.section, libelle: l.libelle, montant: self.montantLigne(l) }) })
        })
        return out
      }
      var lignes = this.detailMateriel('PV').map(function(l) { return { poste: 'PV', libelle: l.libelle, montant: l.montant * self.facteurTvaInstallation } })
      if (this.avecBatterieResult) {
        lignes = lignes.concat(this.detailMateriel('BATTERIE').map(function(l) { return { poste: 'BATTERIE', libelle: l.libelle, montant: l.montant * self.facteurTvaBatterie } }))
      }
      return lignes
    },
    libelleSection: function(section) {
      return { PV: 'CAPEX Installation PV', BATTERIE: 'CAPEX Batterie', PRESTA: 'CAPEX Presta externe',
        MO: "CAPEX Main d'œuvre / Engins", BORNES: 'CAPEX Bornes de charge' }[section] || section
    },
    lignesSection: function(section) {
      var d = this.result && this.result.detailMateriel
      return d ? d.filter(function(l) { return (l.section || 'PV') === section }) : []
    },
    /** Montant affiché d'une ligne : HT pour un professionnel, TVA de la ligne pour un particulier. */
    montantLigne: function(l) {
      if (!this.avecTva) return l.montant
      return l.montant * (1 + (l.tvaPct != null ? l.tvaPct : (this.result && this.result.tauxTva) || 0) / 100)
    },
    arrondiMarge: function(v) { return Math.round(v * 10) / 10 },
    /** Ratio du prix de revient : €/Wc (€/kWh pour la batterie), comme le fichier de Maxime. */
    ratioSection: function(sec) {
      if (sec.section === 'BATTERIE') return this.ratioKwh(sec.achat)
      return this.ratioWc(sec.achat)
    },
    ratioWc: function(v) {
      var wc = this.puissanceKwcResult * 1000
      return wc > 0 ? nombreFr(v / wc, 2) + ' €/Wc' : ''
    },
    ratioKwh: function(v) {
      var kwh = this.batteryCapacityKwh
      return kwh > 0 ? nombreFr(v / kwh, 0) + ' €/kWh' : ''
    },
    /**
     * Marge d'une section modifiée dans le rapport (03/11/2026) : enregistrée dans l'étude, puis toute la matrice est
     * recalculée (moins d'une seconde) ; le scénario affiché reste le même.
     */
    changerMarge: function(section, valeur) {
      var cle = { PV: 'margeMaterielPct', BATTERIE: 'margeBatteriePct', PRESTA: 'margePrestaPct', BORNES: 'margeBornesPct' }[section]
      if (!cle) return
      var v = valeur === '' ? null : parseFloat(String(valeur).replace(',', '.'))
      if (v != null && (isNaN(v) || v < 0 || v >= 100)) { this.erreurMarges = 'Saisissez une marge de 0 à 99 %.'; return }
      var materiel = JSON.parse(JSON.stringify(this.$store.state.wizard.materiel || {}))
      materiel[cle] = v
      this.$store.commit('wizard/SET_STEP4', { materiel: materiel })
      this.recalculerAvecMarges()
    },
    recalculerAvecMarges: function() {
      var self = this
      var cellule = this.selectedMatrixCell ? { kWc: this.selectedMatrixCell.kWc, kWh: this.selectedMatrixCell.kWh } : null
      this.recalculMarges = true
      this.erreurMarges = ''
      var etat = this.$store.state.wizard
      calculerEtude(etat).then(function(res) {
        self.$store.commit('wizard/SET_RESULT', res)
        if (cellule && res && res.matrix) {
          var c = res.matrix.find(function(x) { return x.kwc === cellule.kWc && x.kwh === cellule.kWh })
          if (c) self.$store.commit('wizard/SET_MATRIX_CELL', c)
        }
        return enregistrerEtudeCalculee(self.$store.state.wizard, res)
      }).catch(function(err) {
        var data = err && err.response ? err.response.data : null
        self.erreurMarges = (data && data.error && data.error.message) || (data && data.message) || 'Le recalcul a échoué.'
      }).then(function() { self.recalculMarges = false })
    },
    /** Lignes du chiffrage au matériel d'un poste (PV ou BATTERIE) ; vide avec les grilles de prix. */
    detailMateriel: function(poste) {
      var d = this.result && this.result.detailMateriel
      return d ? d.filter(function(l) { return l.poste === poste }) : []
    },
    formatEur: function(v) {
      if (v === null || v === undefined || isNaN(v)) return '—'
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v)
    },
    formatKwh: function(v) {
      if (v === null || v === undefined || isNaN(v)) return '—'
      return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v)
    },

    matrixCellRoiInflation: function(kwc, kwh) {
      if (!this.matrixResults) return null
      var cell = this.matrixResults.find(function(c) { return c.kwc === kwc && c.kwh === kwh })
      if (!cell || !cell.result) return null
      var r = cell.result
      var coutTtc = r.coutNetTTC != null ? r.coutNetTTC : (r.coutInstallationHT * (1 + (r.tauxTva != null ? r.tauxTva : 20) / 100))
      // paramètres de la cellule elle-même : le ROI affiché ne dépend pas de la cellule sélectionnée
      var param = function(v, defaut) { return v != null ? v : defaut }
      var inflationEco  = 1 + param(r.inflationElec, this.inflationElec) / 100
      var evol = {
        hausseMaintenancePct: param(r.hausseMaintenancePct, this.hausseMaintenancePctResult),
        hausseMaintenancePeriode: param(r.hausseMaintenancePeriode, this.hausseMaintenancePeriodeResult),
        inflationAssurance: param(r.inflationAssurance, this.inflationAssuranceResult),
      }
      var panFactor = 1 - param(r.degradationPanneaux, this.degradationPanneauxResult) / 100
      var batFactor = 1 - param(r.degradationBatterie, this.degradationBatterieResult) / 100
      var ecoDirec = r.economieAutoconsoDirecEur || 0
      var ecoSurp  = r.rachatSurplusRevenu || 0
      var ecoBat   = r.economieBatterieEur || 0
      if ((ecoDirec + ecoSurp + ecoBat) <= 0 || coutTtc <= 0) return null
      var dureeEmprunt = r.dureeEmprunt || 0
      var montantEmprunte = this.avecFinancementStore ? coutTtc * (1 - (r.apportPct || 0) / 100) : 0
      var totalMensualites = (this.avecFinancementStore && r.mensualite && dureeEmprunt) ? r.mensualite * 12 * dureeEmprunt : 0
      var totalCost = coutTtc + totalMensualites - montantEmprunte
      var cumul = -totalCost
      for (var yr = 1; yr <= ANNEES_PROJECTION; yr++) {
        var prev = cumul
        var eco = ecoDirec + (yr <= ANNEES_VENTE_SURPLUS ? ecoSurp : 0) + ecoBat
        var o = evolutionOpex(r.maintenanceAnnuelle || 0, r.assuranceAnnuelle || 0, yr, evol)
        cumul += eco - o.maintenance - o.assurance
        ecoDirec = ecoDirec * inflationEco * panFactor
        ecoSurp  = ecoSurp  * INDEXATION_SURPLUS * panFactor
        ecoBat   = ecoBat   * inflationEco * panFactor * batFactor
        if (cumul >= 0) {
          var fraction = Math.abs(prev) / (Math.abs(prev) + cumul)
          return +((yr - 1) + fraction).toFixed(1)
        }
      }
      return null
    },
    /**
     * Taux d'une cellule (%), mêmes définitions que les titres des donuts (B29) : autoconsommation = (direct + batterie)
     * / production ; autoproduction = (direct + batterie) / consommation.
     */
    matrixCellTaux: function(cle, kwc, kwh) {
      if (!this.matrixResults) return null
      var cell = this.matrixResults.find(function(c) { return c.kwc === kwc && c.kwh === kwh })
      if (!cell || !cell.result) return null
      var r = cell.result
      var solaire = (r.autoconsoDirecKwh || 0) + (r.autoconsoBatterieKwh || 0)
      var total = cle === 'autoconso'
        ? solaire + (r.surplusInjecte || 0) + (r.pertesBatterieKwh || 0)
        : solaire + (r.couvertureReseauKwh || 0)
      return total > 0 ? solaire / total * 100 : null
    },
    tauxColor: function(v) {
      if (v >= 75) return '#2e7d32'
      if (v >= 50) return '#66bb6a'
      if (v >= 25) return '#f9a825'
      return '#e65100'
    },
    celluleCalculee: function(kwc, kwh) {
      return !!(this.matrixResults && this.matrixResults.some(function(c) { return c.kwc === kwc && c.kwh === kwh && c.result }))
    },
    matrixCellRoi: function(kwc, kwh) {
      return this.matrixCellRoiInflation(kwc, kwh)
    },
    matrixRoiColor: function(kwc, kwh) {
      var roi = this.matrixCellRoi(kwc, kwh)
      if (roi === null) return '#aaa'
      if (roi < 8)  return '#2e7d32'
      if (roi < 12) return '#66bb6a'
      if (roi < 16) return '#f9a825'
      if (roi < 20) return '#e65100'
      return '#b71c1c'
    },
    isSelectedCell: function(kwc, kwh) {
      if (!this.selectedMatrixCell) return false
      return this.selectedMatrixCell.kWc === kwc && this.selectedMatrixCell.kWh === kwh
    },
    selectMatrixCell: function(kwc, kwh, evenement) {
      if (!this.matrixResults) return
      var cell = this.matrixResults.find(function(c) { return c.kwc === kwc && c.kwh === kwh })
      if (cell) this.SET_MATRIX_CELL(cell)
      // retour commercial du 20/10/2026 : les autres matrices défilent comme celle où le scénario a été choisi
      var source = evenement && evenement.currentTarget && evenement.currentTarget.closest
        ? evenement.currentTarget.closest('.matrix-table-wrapper') : null
      if (source) this.alignerMatrices(source)
    },
    /** Fait défiler les matrices pour montrer le scénario choisi (présélection hors de la vue par défaut). */
    montrerCelluleChoisie: function() {
      if (!this.$el || !this.$el.querySelector) return
      var cellule = this.$el.querySelector('.matrix-cell--selected')
      var source = cellule ? cellule.closest('.matrix-table-wrapper') : null
      if (!source || !source.clientHeight) return // matrice pas encore affichée
      var entete = source.querySelector('thead')
      var hautEntete = entete ? entete.offsetHeight : 0
      var ligne = cellule.parentNode.querySelector('.matrix-row-header')
      var largeurEntete = ligne ? ligne.offsetWidth : 0
      var deplace = false
      if (cellule.offsetTop - hautEntete < source.scrollTop
          || cellule.offsetTop + cellule.offsetHeight > source.scrollTop + source.clientHeight) {
        source.scrollTop = Math.max(0, cellule.offsetTop - hautEntete - (source.clientHeight - hautEntete - cellule.offsetHeight) / 2)
        deplace = true
      }
      if (cellule.offsetLeft - largeurEntete < source.scrollLeft
          || cellule.offsetLeft + cellule.offsetWidth > source.scrollLeft + source.clientWidth) {
        source.scrollLeft = Math.max(0, cellule.offsetLeft - largeurEntete)
        deplace = true
      }
      if (deplace) this.alignerMatrices(source)
    },
    /** Même position de défilement (horizontal et vertical) pour toutes les matrices que pour `source`. */
    alignerMatrices: function(source) {
      var tableaux = this.$el.querySelectorAll('.matrix-table-wrapper')
      for (var i = 0; i < tableaux.length; i++) {
        if (tableaux[i] === source) continue
        tableaux[i].scrollLeft = source.scrollLeft
        tableaux[i].scrollTop = source.scrollTop
      }
    },
  },
  mounted: function() {
    // Présélection : la cellule qui affiche le plus petit ROI dans la matrice (B30) ; à égalité, le ROI simplifié
    // du serveur départage. Sans ROI affiché, repli sur le ROI simplifié seul.
    if (this.matrixResults && this.matrixResults.length) {
      var self = this
      var valid = function(c) { return c.result && c.result.roi > 0 && c.result.roi < 1e15 }
      var best = null, bestShown = null
      this.matrixResults.forEach(function(c) {
        if (!valid(c)) return
        var shown = self.matrixCellRoi(c.kwc, c.kwh)
        if (shown === null) shown = Infinity
        if (!best || shown < bestShown || (shown === bestShown && c.result.roi < best.result.roi)) {
          best = c
          bestShown = shown
        }
      })
      // aucun scénario rentable (12/10/2026) : le scénario retenu par le calcul (meilleures économies nettes) est présélectionné
      if (!best && this.simulationResult) {
        var r = this.simulationResult
        best = this.matrixResults.find(function(c) { return c.result === r || (c.result && c.kwc === r.puissanceOptimaleKwc && c.kwh === r.batteryCapacityKwh) }) || null
      }
      if (best) this.SET_MATRIX_CELL(best)
      this.$nextTick(this.montrerCelluleChoisie)
    }
    this.chargerStatsCourbe()
  },
}
</script>

<style scoped>
.rapport-site { font-size: .95rem; color: #666; margin-top: 4px; }
.section-title__note { font-weight: 400; font-size: 12px; color: #888; margin-left: 6px; text-transform: none; letter-spacing: 0; }
.marges-rapport { margin: 0 0 16px; padding: 12px 16px; background: #f4f7fb; border: 1px solid #c9d6e8; border-radius: 10px; font-size: 13px; }
.marges-rapport__titre { font-weight: 700; color: #1a1a2e; margin-bottom: 8px; display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; }
.marges-rapport__note { font-weight: 400; color: #666; font-size: 12px; }
.marges-rapport__calcul { color: #1565c0; font-weight: 600; }
.marges-rapport__corps { display: flex; gap: 24px; flex-wrap: wrap; align-items: flex-start; }
.marges-table { border-collapse: collapse; min-width: 560px; }
.marges-table th { text-align: left; font-weight: 600; color: #555; padding: 4px 12px 4px 0; border-bottom: 1px solid #c9d6e8; }
.marges-table td { padding: 4px 12px 4px 0; color: #222; }
.marges-table tfoot td { border-top: 1px solid #e0e6ef; }
.marges-total td { font-weight: 700; }
.marges-ratio { color: #666; font-size: 12px; margin-left: 6px; }
.marge-input { width: 60px; padding: 2px 4px; border: 1px solid #bbb; border-radius: 4px; }
.marge-input--sous { border-color: #e65100; background: #fff3e0; }
.marge-avertissement { color: #e65100; font-size: 12px; margin-left: 6px; font-weight: 600; }
.marge-sans { color: #888; }
.marges-resume { font-size: 12px; color: #333; max-width: 420px; }
.marges-resume__titre { font-weight: 700; margin: 0 0 4px; }
.marges-resume dl { display: grid; grid-template-columns: auto 1fr; gap: 2px 10px; margin: 0 0 8px; }
.marges-resume dt { color: #666; }
.marges-resume dd { margin: 0; }
.marges-resume ul { margin: 0; padding-left: 16px; }
.marges-erreur { color: #c62828; margin: 6px 0 0; }
.cost-row.cost-row--section span { font-weight: 700; color: #1a1a2e; }
.hypotheses-modifiees { margin: 0 0 16px; padding: 12px 16px; background: #fff8e6; border: 1px solid #e0c27a; border-radius: 10px; font-size: 13px; }
.hypotheses-modifiees__titre { font-weight: 700; color: #8a5a00; margin-bottom: 6px; }
.hypotheses-modifiees table { border-collapse: collapse; }
.hypotheses-modifiees th { text-align: left; font-weight: 600; color: #8a5a00; padding: 2px 16px 2px 0; }
.hypotheses-modifiees td { padding: 2px 16px 2px 0; color: #3a3f4b; }
.hypotheses-modifiees__valeur { font-weight: 700; }
.hypotheses-modifiees__lien { border: none; background: none; color: #1565c0; text-decoration: underline; cursor: pointer; font-size: 13px; padding: 0; }
/* ══════════════════════════════════════════════════════
   Caractéristiques / Investissement & Exploitation / Indicateurs
   ══════════════════════════════════════════════════════ */
.outer-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: stretch;
  gap: 16px;
  margin-bottom: 20px;
}
.left-col { display: flex; flex-direction: column; gap: 16px; }

.info-block {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  box-sizing: border-box;
}
.info-block--fill { flex: 1; display: flex; flex-direction: column; }
.info-block--stretch { height: 100%; display: flex; flex-direction: column; }

.info-block__title {
  font-size: 12px;
  font-weight: 700;
  color: #1a1a2e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f5c518;
  margin-bottom: 14px;
}

/* Spec cards */
.spec-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.spec-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 10px 12px;
}
.spec-card--wide { grid-column: span 2; }
.spec-card__label { font-size: 10px; font-weight: 600; color: #999; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
.spec-card__value { font-size: 16px; font-weight: 700; color: #1a1a2e; }
.spec-card__sub { font-size: 11px; color: #888; margin-top: 2px; }

/* Cost sections */
.cost-section { margin-bottom: 4px; }
.cost-section__header {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 4px 10px;
  border-radius: 4px;
  margin-bottom: 8px;
}
.cost-section__header--capex { background: #1a1a2e; color: #fff; }
.cost-section__header--opex  { background: #e65100; color: #fff; }

.cost-rows { display: flex; flex-direction: column; gap: 2px; }
.cost-row.cost-row--detail {
  font-size: 11.5px;
  color: #777;
  padding-left: 12px;
}
.cost-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #444;
  padding: 5px 0;
  border-bottom: 1px solid #f5f5f5;
}
.cost-row span:last-child { font-weight: 600; color: #222; }
.cost-row--total { border-top: 1px solid #ddd; border-bottom: none; font-weight: 700; padding-top: 7px; margin-top: 2px; }
.cost-row--total span { font-weight: 700; color: #1a1a2e; }

.inflation-notice {
  font-size: 11px;
  color: #777;
  font-style: italic;
  margin-top: 12px;
  padding: 6px 10px;
  background: #fffde7;
  border-radius: 4px;
}

/* KPI grid Indicateurs */
.kpi-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  flex: 1;
}
.kpi-2 {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.kpi-2--roi   { background: rgba(245,197,24,0.08); border: 1px solid #f5c518; }
.kpi-2--green { background: #e8f5e9; }

.kpi-2__label { font-size: 10px; font-weight: 600; color: #999; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
.kpi-2__value { font-size: 18px; font-weight: 700; color: #1a1a2e; }
.kpi-2__value--green { color: #2e7d32; }
.kpi-2__value--red   { color: #c62828; }
.kpi-2__sub { font-size: 11px; color: #888; margin-top: 2px; }

/* ══════════════════════════════════════════════════════ */

.rapport-page {
  font-family: 'Raleway', sans-serif;
  background: #f0f2f5;
  min-height: calc(100vh - 140px);
}

/* En-tête */
.rapport-header {
  background: #fff;
  padding: 16px 32px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 10;
}
.rapport-header__top { display: flex; justify-content: space-between; align-items: center; }
.rapport-title { font-size: 20px; font-weight: 700; color: #1a1a2e; margin: 0 0 2px 0; }
.rapport-project-name { font-size: 15px; font-weight: 600; color: #888; }
.btn-pdf {
  padding: 8px 20px;
  border: 2px solid #1a1a2e;
  border-radius: 20px;
  background: #fff;
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.btn-pdf:hover:not(:disabled) { background: #1a1a2e; color: #fff; }
.btn-pdf:disabled { opacity: 0.6; cursor: wait; }


/* Layout */
.rapport-layout { display: flex; align-items: flex-start; }

/* Sidebar */
.rapport-sidebar {
  width: 340px;
  flex-shrink: 0;
  position: sticky;
  top: 73px;
  align-self: flex-start;
  background: #fff;
  border-right: 1px solid #eee;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: calc(100vh - 73px);
  overflow-y: auto;
  max-height: calc(100vh - 73px);
}
.sidebar-block { display: flex; flex-direction: column; gap: 8px; }
.sidebar-block__title {
  font-size: 11px; font-weight: 700; color: #1a1a2e;
  text-transform: uppercase; letter-spacing: 0.5px;
  padding-bottom: 6px; border-bottom: 2px solid #f5c518;
}
.sidebar-block__hint { font-size: 10px; color: #999; font-style: italic; }
.sidebar-block--warning { background: #fff8e1; border: 1px solid #f9a825; border-radius: 8px; padding: 12px; gap: 6px; }
.warning-icon { font-size: 20px; text-align: center; color: #f57c00; }
.warning-text { font-size: 11px; color: #795548; line-height: 1.5; text-align: center; }

/* Matrice */
.matrix-axis-label { font-size: 10px; font-weight: 600; color: #1976d2; font-style: italic; }
.matrix-axis-label--top { text-align: center; margin-bottom: 4px; }
.matrix-axis-label--left { writing-mode: vertical-rl; transform: rotate(180deg); margin-right: 4px; white-space: nowrap; font-size: 9px; }
.matrix-layout { display: flex; align-items: center; }
/* 21/10/2026 : jusqu'à 25 lignes de puissances : défilement vertical, en-têtes de lignes et de colonnes fixes */
.matrix-table-wrapper { overflow: auto; width: 100%; max-height: 320px; }
/* bordures séparées : avec des bordures fusionnées, les lignes défilantes transparaissent au bord des en-têtes fixes */
.matrix-table-wrapper table { border-collapse: separate; border-spacing: 0; }
.matrix-table-wrapper thead th { position: sticky; top: 0; z-index: 2; }
.matrix-table-wrapper .matrix-row-header { position: sticky; left: 0; z-index: 1; }
.matrix-table-wrapper thead .matrix-corner { left: 0; z-index: 3; }
/* 26/10/2026 : mêmes largeurs de colonnes dans les trois matrices, quel que soit le contenu (« > 20 » plus large que
   « 48 ») — sinon les colonnes ne tombent plus les unes sous les autres une fois les matrices alignées */
.matrix-table-wrapper table { table-layout: fixed; width: max-content; min-width: 100%; font-size: 12px; }
.matrix-table-wrapper .matrix-corner { width: 38px; min-width: 38px; }
.matrix-table-wrapper .matrix-col-header { width: 44px; min-width: 44px; }
.matrix-table { border-collapse: collapse; width: 100%; }
.matrix-corner { background: #1a1a2e; padding: 3px 6px; border: 1px solid #333; min-width: 20px; }
.matrix-col-header { background: #1a1a2e; color: #fff; font-weight: 700; padding: 3px 6px; text-align: center; border: 1px solid #333; font-size: 10px; min-width: 34px; }
.matrix-row-header { background: #1a1a2e; color: #fff; font-weight: 700; padding: 3px 6px; text-align: center; border: 1px solid #333; font-size: 10px; }
.matrix-cell { padding: 4px 6px; text-align: center; border: 1px solid #e8f4ff; background: #f8fcff; cursor: pointer; transition: background 0.1s; }
.matrix-cell:hover { background: #e3f2fd; }
.matrix-cell--selected { background: #e3f2fd !important; outline: 2px solid #1976d2; outline-offset: -1px; position: relative; z-index: 1; }
/* matrices des taux (même présentation que la matrice des ROI) */
.repartition-periodes { margin-top: 10px; font-size: 13px; color: #555; display: flex; flex-wrap: wrap; gap: 4px 14px; }
.repartition-periodes__item b { color: #1a237e; }
.taux-table { border-collapse: collapse; width: 100%; font-size: 12px; }
.taux-cell { padding: 4px 6px; text-align: center; border: 1px solid #e8f4ff; background: #f8fcff; cursor: pointer; transition: background 0.1s; }
.taux-cell:hover { background: #e3f2fd; }
.taux-cell--selected { background: #e3f2fd !important; outline: 2px solid #1976d2; outline-offset: -1px; position: relative; z-index: 1; }
.matrix-roi { font-weight: 700; font-size: 11px; display: block; }
.matrix-roi--na { color: #ccc; font-weight: 400; }
.matrix-roi--non-rentable { color: #b71c1c; font-weight: 700; white-space: nowrap; }
.matrix-legend-row { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; justify-content: center; }
.legend-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.legend-item { display: flex; align-items: center; gap: 3px; font-size: 9px; color: #666; font-weight: 600; }
.matrix-hint-click { font-size: 9px; color: #bbb; font-style: italic; text-align: center; margin-top: 4px; }

/* Corps */
.rapport-main { flex: 1; min-width: 0; }
.rapport-body { padding: 20px; display: flex; flex-direction: column; gap: 20px; }

/* KPI Grid */
.kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
.kpi-card {
  background: #fff; border-radius: 12px; padding: 16px 18px;
  border-left: 4px solid #eee; box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}
.kpi-card--primary { border-left-color: #1a1a2e; background: #f8f8fc; }
.kpi-card--roi { border-left-color: #f5c518; }
.kpi-card--green { border-left-color: #4caf50; }
.kpi-card__label { font-size: 11px; color: #888; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 6px; }
.kpi-card__value { font-size: 22px; font-weight: 800; color: #1a1a2e; line-height: 1.1; }
.kpi-card__value--green { color: #4caf50; }
.kpi-card__sub { font-size: 11px; color: #aaa; margin-top: 4px; line-height: 1.4; }

/* Sections */
.rapport-section { background: #fff; border-radius: 12px; padding: 20px; box-shadow: 0 2px 6px rgba(0,0,0,0.05); }
.rapport-section--grow { flex: 1; min-width: 0; }
.rapport-section--fixed { width: 300px; flex-shrink: 0; }
.rapport-section--half { flex: 1; min-width: 0; }
.section-row { display: flex; gap: 16px; align-items: stretch; }
.section-title {
  font-size: 12px;
  font-weight: 700;
  color: #1a1a2e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f5c518;
  margin-bottom: 14px;
}
.section-header-row { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #f5c518; margin-bottom: 14px; }
.section-header-row .section-title { margin-bottom: 0; padding-bottom: 10px; border-bottom: none; }
.subsection-title { font-size: 13px; font-weight: 700; color: #333; margin-bottom: 10px; }

/* Sélecteur de mois */
.jour-type-legende { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 6px 14px; font-size: 11px; color: #444; margin: 6px 0 4px; }
.jour-type-legende__item { display: inline-flex; align-items: center; gap: 5px; }
.jour-type-legende__anneau { width: 9px; height: 9px; border: 2px solid; border-radius: 3px; background: #fff; }
.jour-type-legende__trait { width: 18px; height: 0; border-top: 2px dashed; }
.month-select { border: 1px solid #ddd; border-radius: 8px; padding: 4px 8px; font-size: 11px; font-family: 'Raleway', sans-serif; color: #333; background: #fff; outline: none; }

/* Donuts */
.donut-subtitle { font-size: 12px; color: #666; margin-bottom: 12px; }
.donut-row { display: flex; gap: 16px; align-items: center; }
.donut-chart-wrap { width: 160px; flex-shrink: 0; }
.donut-legend { display: flex; flex-direction: column; gap: 8px; flex: 1; }
.donut-legend--compact { margin-top: 10px; }
.donut-legend-item { display: flex; align-items: center; gap: 8px; font-size: 11px; color: #444; line-height: 1.4; }
.donut-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.donut-legend-note { font-size: 11px; color: #666; font-style: italic; margin-top: 4px; line-height: 1.4; cursor: help; }

/* Bilan + factures */
.bilan-demande-row { display: grid; grid-template-columns: 1.6fr 1fr; gap: 20px; }
.factures-chart-card { display: flex; flex-direction: column; }
.bilan-table-card { display: flex; flex-direction: column; }
.bilan-table { width: 100%; border-collapse: collapse; }
.bilan-table td { padding: 5px 8px; font-size: 12px; border-bottom: 1px solid #f5f5f5; }
.tr-after td { background: #fafafa; }
.tr-eco td { font-weight: 700; }
.tr-sub td { font-size: 11px; color: #888; }
.tr-total td { border-top: 2px solid #eee; font-size: 13px; padding-top: 8px; }
.text-right { text-align: right; white-space: nowrap; }
.text-green { color: #4caf50; }
.text-red { color: #e53935; }

/* Tableau amortissement */
.amort-partie + .amort-partie { margin-top: 16px; }
.amort-partie__titre { font-weight: 700; font-size: 13px; color: #1a237e; margin: 0 0 6px; }
.amort-table-wrapper { overflow-x: auto; }
.amort-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.amort-table th { background: #f5c518; color: #222; padding: 8px 10px; text-align: center; font-weight: 700; font-size: 11px; white-space: nowrap; }
.amort-table td { padding: 6px 10px; text-align: center; border-bottom: 1px solid #f5f5f5; white-space: nowrap; }
.row-roi { background: #fffde7; font-weight: 700; }
.row-total td { background: #e8f0fe; font-weight: 700; font-size: 14px; }

.no-data-msg { font-size: 12px; color: #aaa; text-align: center; padding: 20px; }
.no-result { text-align: center; padding: 60px; color: #888; }
.btn-back-calc { margin-top: 16px; padding: 10px 28px; border: 1px solid #ccc; border-radius: 20px; background: #fff; color: #555; font-size: 14px; font-family: 'Raleway', sans-serif; cursor: pointer; }

/* écrans de portable (13/10/2026) : sidebar au-dessus du rapport, matrices lisibles, tableau défilant */
@media (max-width: 1200px) {
  .rapport-layout { flex-direction: column; align-items: stretch; }
  .rapport-sidebar {
    width: 100%; position: static; min-height: 0; max-height: none; border-right: 0; border-bottom: 1px solid #eee;
    display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; align-items: start;
  }
}
.amort-table { min-width: 760px; }
@media print {
  .rapport-sidebar { display: none; }
  .rapport-header { position: static; }
}
</style>
